// src/components/Controlling.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  loadResourcePlanning,
  loadAllResourcePlanning,
} from "../services/azureResourcePlanningService";
import { getTimekeepingData } from "../services/azureTimekeepingService";
import {
  getProjectPhases,
  saveBulkPhases,
  deletePhase,
} from "../services/azureProjectPhasesService";
import { addWeeks, getWeekDays } from "../utils/dateUtils";
import * as ProjectsService from "../services/azureProjectsService";
import { getAllTeamMembers } from "../services/azureTeamService";
import { findTeamMember, getTeamMemberInitials } from "../utils/teamLookup";
import { getProjectAssignments, saveProjectAssignments } from "../services/azureProjectAssignmentService";
import { getBudgetEntriesForProject, saveBudgetEntry } from "../services/azureBudgetService";
import { getProducts } from "../services/apiService";
import { getProjectRates, saveRate as saveProjectRate } from "../services/azureRatesService";
import BudgetVerwaltung from "./BudgetVerwaltung";
import ProjectRates from "./ProjectRates";
import ZahlungsplanModal from "./ZahlungsplanModal";
import PendenzMembersModal from "./PendenzMembersModal";
import PartnerAssignField from "./PartnerAssignField";
import { RELATION_TYPES } from "../services/azureRelationsService";
import { useAuth } from "../hooks/useAuth";
import { canSetLocalOnly } from "../utils/permissions";
import { formatCurrency } from "../utils/formatUtils";
import {
  getISOWeekNumber,
  getWeekStartDate,
  getWeekEndDate,
  parseLocalDate,
  getEffectiveWeekForDate,
} from "./zahlungsplan/zahlungsplanUtils";

// Rollen für die Firmen-Zuteilung (gleiche Rollen wie im Netzwerk-Tab)
const PARTNER_ROLE_OPTIONS = [
  { value: RELATION_TYPES.AUFTRAGGEBERIN, label: "Auftraggeberin" },
  { value: RELATION_TYPES.PARTNER, label: "Partner" },
  { value: RELATION_TYPES.CONSULTANT, label: "Consultant" },
  { value: RELATION_TYPES.CONTRACTOR, label: "Contractor" },
];

// Default project color (will be overridden by project.color if available)
const DEFAULT_PROJECT_COLOR = "#3b82f6";

// Generate shade variations from a base color
const generateColorShades = (baseColor, count) => {
  // Parse hex color to RGB
  const hex = baseColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const shades = [];
  for (let i = 0; i < count; i++) {
    // Create variations by adjusting lightness
    const factor = 0.4 + (i * 0.6) / Math.max(count - 1, 1);
    const newR = Math.round(r * factor + (255 - r * factor) * (1 - factor) * 0.3);
    const newG = Math.round(g * factor + (255 - g * factor) * (1 - factor) * 0.3);
    const newB = Math.round(b * factor + (255 - b * factor) * (1 - factor) * 0.3);
    shades.push(`rgb(${Math.min(255, newR)}, ${Math.min(255, newG)}, ${Math.min(255, newB)})`);
  }
  return shades;
};

const Controlling = ({
  employees: propEmployees,
  project: propProject,
  projectCode: propProjectCode,
  isInline = false,
  allProjects: propAllProjects,
  onProjectChange,
  readOnly = false
}) => {
  const { projectCode: urlProjectCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userCanSetLocalOnly = canSetLocalOnly(user);

  // Use prop projectCode if provided, otherwise use URL param
  const projectCode = propProjectCode || propProject?.code || urlProjectCode;

  const [project, setProject] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const [projectAssignments, setProjectAssignments] = useState([]);
  const [resourceData, setResourceData] = useState({});
  const [actualHoursData, setActualHoursData] = useState({});
  const [phases, setPhases] = useState([]);
  const [budgetData, setBudgetData] = useState({});
  const [teamMembersData, setTeamMembersData] = useState([]);
  const [expandedPerson, setExpandedPerson] = useState(null);
  const [hoveredPerson, setHoveredPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startWeekOffset, setStartWeekOffset] = useState(0);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [phaseClickWeek, setPhaseClickWeek] = useState(null);
  const [showPersonRows, setShowPersonRows] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [selectedPersonForRate, setSelectedPersonForRate] = useState(null);
  const [personRates, setPersonRates] = useState({});
  const [expandedFinancePhases, setExpandedFinancePhases] = useState([]);
  const [allTimekeepingData, setAllTimekeepingData] = useState([]); // Raw timekeeping entries for Finanzen
  const [allResourceData, setAllResourceData] = useState({}); // All resource planning data
  const [products, setProducts] = useState([]); // AbaNinja products for external rates
  const [showExternalRateModal, setShowExternalRateModal] = useState(false);
  const [selectedFinancePerson, setSelectedFinancePerson] = useState(null);
  const [allBudgetEntries, setAllBudgetEntries] = useState([]);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budgetModalPreset, setBudgetModalPreset] = useState(null);
  const [expandedDrittkostenWeek, setExpandedDrittkostenWeek] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null); // 'budget', 'ressourcen', or null
  const [editingPartnerBudget, setEditingPartnerBudget] = useState(null); // { phaseId, partnerId }
  const [editingMilestone, setEditingMilestone] = useState(null); // { phaseId, weekNumber, year }
  const [milestoneInputValue, setMilestoneInputValue] = useState("");
  const [showPhaseRateModal, setShowPhaseRateModal] = useState(false);
  const [selectedPhaseForRate, setSelectedPhaseForRate] = useState(null);
  const [hoveredEditCell, setHoveredEditCell] = useState(null); // { personId, weekNumber, year }
  const [useExternalRate, setUseExternalRate] = useState(true); // Toggle between Intern (false) and Extern (true) rate view - default to External
  const [showZahlungsplanModal, setShowZahlungsplanModal] = useState(false);
  const [showAnsaetzeModal, setShowAnsaetzeModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showFirmenModal, setShowFirmenModal] = useState(false);

  // Toggle section - only one can be open at a time
  const toggleSection = (section) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const saveTimeoutRef = useRef(null);
  const currentWeekRef = useRef(null);
  const timelineWrapperRef = useRef(null);

  const VISIBLE_WEEKS = 52;
  const HOURS_PER_WEEK = 42;

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentWeek = getISOWeekNumber(today);

  // Wochen generieren
  const weeks = useMemo(() => {
    const result = [];
    const baseDate = new Date();
    baseDate.setDate(baseDate.getDate() + startWeekOffset * 7);

    for (let i = 0; i < VISIBLE_WEEKS; i++) {
      const weekDate = addWeeks(baseDate, i);
      const weekNum = getISOWeekNumber(weekDate);
      const year = weekDate.getFullYear();
      const weekDays = getWeekDays(weekDate);

      result.push({
        number: weekNum,
        year,
        startDate: weekDays[0],
        isCurrentWeek: weekNum === currentWeek && year === currentYear,
      });
    }

    return result;
  }, [startWeekOffset, currentWeek, currentYear]);

  // Get date range for current weeks view
  const getDateRangeForWeeks = useCallback(() => {
    if (weeks.length === 0) return { dateFrom: null, dateTo: null };

    const firstWeek = weeks[0];
    const lastWeek = weeks[weeks.length - 1];

    const dateFrom = new Date(firstWeek.startDate);
    dateFrom.setDate(dateFrom.getDate() - dateFrom.getDay() + 1);

    const dateTo = new Date(lastWeek.startDate);
    dateTo.setDate(dateTo.getDate() - dateTo.getDay() + 7);

    const formatDate = (d) => d.toISOString().split("T")[0];

    return {
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo),
    };
  }, [weeks]);

  // Aggregate timekeeping data by week/employee
  const aggregateTimekeepingData = useCallback((timekeepingEntries, projectCode) => {
    const aggregated = {}; // { KWxx_year: { employeeUuid: hours } }

    timekeepingEntries.forEach((entry) => {
      if (!entry.date || entry.code !== projectCode) return;

      const date = new Date(entry.date);
      const weekNum = getISOWeekNumber(date);
      const year = date.getFullYear();
      const weekKey = `KW${weekNum}_${year}`;

      const employeeId = entry.employeeUuid;
      const hours = entry.durationHours + (entry.durationMinutes / 60);

      if (!aggregated[weekKey]) {
        aggregated[weekKey] = {};
      }
      if (!aggregated[weekKey][employeeId]) {
        aggregated[weekKey][employeeId] = 0;
      }
      aggregated[weekKey][employeeId] += hours;
    });

    return aggregated;
  }, []);

  // Load actual hours from timekeeping (for visible weeks in timeline)
  const loadActualHours = useCallback(async (projectCode) => {
    const { dateFrom, dateTo } = getDateRangeForWeeks();
    if (!dateFrom || !dateTo || !projectCode) return;

    try {
      const timekeepingData = await getTimekeepingData(dateFrom, dateTo, projectCode);
      const aggregated = aggregateTimekeepingData(timekeepingData, projectCode);
      setActualHoursData(aggregated);
    } catch (err) {
      console.error("Error loading actual hours:", err);
    }
  }, [getDateRangeForWeeks, aggregateTimekeepingData]);

  // Load ALL timekeeping data for the project (for Finanzen section)
  const loadAllProjectTimekeeping = useCallback(async (projectCode) => {
    if (!projectCode) return;

    try {
      // Load a wide date range (5 years back, 2 years forward) to capture all project data
      const dateFrom = new Date();
      dateFrom.setFullYear(dateFrom.getFullYear() - 5);
      const dateTo = new Date();
      dateTo.setFullYear(dateTo.getFullYear() + 2);

      const formatDate = (d) => d.toISOString().split("T")[0];
      const timekeepingData = await getTimekeepingData(formatDate(dateFrom), formatDate(dateTo), projectCode);
      setAllTimekeepingData(timekeepingData);
    } catch (err) {
      console.error("Error loading all project timekeeping:", err);
    }
  }, []);

  useEffect(() => {
    if (projectCode) {
      loadData();
    }
  }, [projectCode, propProject?.code]);

  useEffect(() => {
    // Only add wide-page class if not inline (standalone page)
    if (!isInline) {
      document.body.classList.add("wide-page");
      return () => document.body.classList.remove("wide-page");
    }
  }, [isInline]);

  // Reload actual hours only when user navigates weeks (startWeekOffset changes)
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!project) return;
    // Skip the first trigger — loadData already fetches actual hours on initial load
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      return;
    }
    loadActualHours(project.code);
  }, [startWeekOffset]);


  const loadData = async () => {
    try {
      setLoading(true);

      const [projectsData, teamMembers] = await Promise.all([
        ProjectsService.getAllProjects(),
        getAllTeamMembers(),
      ]);

      setAllProjects(projectsData);
      setTeamMembersData(teamMembers);

      // Use prop project if provided, otherwise find by code
      let currentProject = propProject;
      if (!currentProject) {
        currentProject = projectsData.find((p) => p.code === projectCode);
      }
      if (!currentProject) {
        setError("Projekt nicht gefunden");
        setLoading(false);
        return;
      }
      setProject(currentProject);

      // Load all project-specific data in parallel
      const [projectResourceData, resourceDataAll, phasesData, assignments, projectBudgets, productsData, internalRates] = await Promise.all([
        loadResourcePlanning(projectCode),
        loadAllResourcePlanning(),
        getProjectPhases(projectCode),
        getProjectAssignments(projectCode),
        getBudgetEntriesForProject(projectCode),
        getProducts(),
        getProjectRates(projectCode),
        // Fire-and-forget: these update state directly and don't need their results here
        loadAllProjectTimekeeping(projectCode),
        loadActualHours(projectCode),
      ]);

      // Store products for the external rate modal
      setProducts(productsData);

      setResourceData(projectResourceData);

      // Calculate external rates: new internal rates (Ansätze) take priority, then legacy AbaNinja
      const rates = {};
      assignments.forEach(assignment => {
        if (assignment.productUuid) {
          const product = productsData.find(p => p.uuid === assignment.productUuid);
          if (product) {
            rates[assignment.employeeUuid] = product.cost || 0;
          }
        }
      });
      // Override with new internal project rates (default rates)
      internalRates.forEach(r => {
        if (r.phaseId === "default" && r.rate > 0) {
          rates[r.employeeId] = r.rate;
        }
      });
      setPersonRates(rates);
      setAllResourceData(resourceDataAll); // Store ALL projects' resource data for remaining hours calculation
      setPhases(phasesData);
      setProjectAssignments(assignments);

      setBudgetData(projectBudgets);
      setAllBudgetEntries(projectBudgets);
    } catch (err) {
      console.error("Fehler beim Laden:", err);
      setError("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  // Get persons working on this project (from resource planning or assignments)
  const projectPersons = useMemo(() => {
    const personIds = new Set();

    // From resource planning
    Object.values(resourceData).forEach((weekData) => {
      Object.keys(weekData).forEach((personId) => {
        if (weekData[personId] > 0) {
          personIds.add(personId);
        }
      });
    });

    // From actual hours
    Object.values(actualHoursData).forEach((weekData) => {
      Object.keys(weekData).forEach((personId) => {
        if (weekData[personId] > 0) {
          personIds.add(personId);
        }
      });
    });

    // From assignments
    projectAssignments.forEach((assignment) => {
      personIds.add(assignment.employeeUuid);
    });

    return Array.from(personIds);
  }, [resourceData, actualHoursData, projectAssignments]);

  // Get hours for person/week
  const getPersonHoursForWeek = useCallback(
    (weekNumber, year, personUuid) => {
      const weekKey = `KW${weekNumber}_${year}`;
      return resourceData[weekKey]?.[personUuid] || 0;
    },
    [resourceData]
  );

  // Get actual hours for person/week
  const getActualPersonHoursForWeek = useCallback(
    (weekNumber, year, personUuid) => {
      const weekKey = `KW${weekNumber}_${year}`;
      return actualHoursData[weekKey]?.[personUuid] || 0;
    },
    [actualHoursData]
  );

  // Get total planned hours for week
  const getTotalPlannedForWeek = useCallback(
    (weekNumber, year) => {
      const weekKey = `KW${weekNumber}_${year}`;
      const weekData = resourceData[weekKey];
      if (!weekData) return 0;
      return Object.values(weekData).reduce((sum, h) => sum + h, 0);
    },
    [resourceData]
  );

  // Get total actual hours for week
  const getTotalActualForWeek = useCallback(
    (weekNumber, year) => {
      const weekKey = `KW${weekNumber}_${year}`;
      const weekData = actualHoursData[weekKey];
      if (!weekData) return 0;
      return Object.values(weekData).reduce((sum, h) => sum + h, 0);
    },
    [actualHoursData]
  );

  // Check if week is in the past
  const isWeekInPast = useCallback(
    (weekNumber, year) => {
      if (year < currentYear) return true;
      if (year > currentYear) return false;
      return weekNumber < currentWeek;
    },
    [currentWeek, currentYear]
  );

  // Get pensum for a person (from team members data or default 100%)
  const getPersonPensum = useCallback(
    (personId) => {
      const teamMember = teamMembersData.find(
        (tm) => tm.employeeUuid === personId,
      );
      return teamMember?.pensum ?? 100;
    },
    [teamMembersData]
  );

  // Get max hours per week for a person based on pensum
  const getPersonMaxHours = useCallback(
    (personId) => {
      const pensum = getPersonPensum(personId);
      return (pensum / 100) * HOURS_PER_WEEK;
    },
    [getPersonPensum]
  );

  // Get total hours for a person in a week across ALL projects
  const getPersonTotalHoursForWeek = useCallback(
    (personId, weekNumber, year) => {
      const weekKey = `KW${weekNumber}_${year}`;
      let total = 0;

      Object.keys(allResourceData).forEach((projCode) => {
        const hours = allResourceData[projCode]?.[weekKey]?.[personId] || 0;
        total += hours;
      });

      return total;
    },
    [allResourceData]
  );

  // Get remaining hours for a person in a week (max hours - total hours across all projects)
  const getPersonRemainingHours = useCallback(
    (personId, weekNumber, year) => {
      const maxHours = getPersonMaxHours(personId);
      const totalHours = getPersonTotalHoursForWeek(personId, weekNumber, year);
      return maxHours - totalHours;
    },
    [getPersonMaxHours, getPersonTotalHoursForWeek]
  );

  // Check if a phase covers a specific week
  const isPhaseInWeek = useCallback((phase, weekNumber, year) => {
    if (!phase.startDate || !phase.endDate) return false;

    const weekDate = weeks.find(
      (w) => w.number === weekNumber && w.year === year
    )?.startDate;
    if (!weekDate) return false;

    const start = new Date(phase.startDate);
    const end = new Date(phase.endDate);
    return weekDate >= start && weekDate <= end;
  }, [weeks]);

  // Project color for all phases and person shades
  const projectColor = useMemo(() => {
    return project?.color || DEFAULT_PROJECT_COLOR;
  }, [project]);

  // Generate person color shades from project color
  const personColorShades = useMemo(() => {
    const count = Math.max(projectPersons.length, 1);
    return generateColorShades(projectColor, count);
  }, [projectColor, projectPersons.length]);

  const getPersonColor = useCallback((personId) => {
    const idx = projectPersons.indexOf(personId);
    return personColorShades[idx % personColorShades.length] || projectColor;
  }, [projectPersons, personColorShades, projectColor]);

  // All phases use the same project color
  const getPhaseColor = useCallback(() => {
    return projectColor;
  }, [projectColor]);

  const getEmployeeName = (personId) => {
    const member = findTeamMember(teamMembersData, personId);
    if (!member) {
      if (personId) {
        console.warn(
          "[Controlling] Lookup fehlgeschlagen für personId:",
          personId,
          "verfügbare Schlüssel:",
          teamMembersData.length
        );
      }
      return "Unbekannt (gelöscht)";
    }
    return member.name || "Unbekannt (gelöscht)";
  };

  const getEmployeeInitials = (personId) =>
    getTeamMemberInitials(teamMembersData, personId);

  // Get internal rate for a person from team data
  const getInternalRate = useCallback((personId) => {
    const member = findTeamMember(teamMembersData, personId);
    return member?.internalRate || 0;
  }, [teamMembersData]);

  // Check if a date falls within a phase
  const isDateInPhase = useCallback((date, phase) => {
    if (!phase.startDate || !phase.endDate) return false;
    const checkDate = new Date(date);
    const start = new Date(phase.startDate);
    const end = new Date(phase.endDate);
    return checkDate >= start && checkDate <= end;
  }, []);

  // Calculate hours worked per phase per person (uses ALL timekeeping data)
  const getPhaseActualHours = useCallback((phase) => {
    const result = { total: 0, byPerson: {} };

    if (!phase.startDate || !phase.endDate) return result;

    const phaseStart = new Date(phase.startDate);
    const phaseEnd = new Date(phase.endDate);

    // Use raw timekeeping entries for accurate date-based filtering
    allTimekeepingData.forEach((entry) => {
      if (!entry.date) return;

      const entryDate = new Date(entry.date);
      if (entryDate < phaseStart || entryDate > phaseEnd) return;

      const personId = entry.employeeUuid;
      const hours = entry.durationHours + (entry.durationMinutes / 60);

      result.total += hours;
      result.byPerson[personId] = (result.byPerson[personId] || 0) + hours;
    });

    return result;
  }, [allTimekeepingData]);

  // Calculate hours budgeted per phase per person (uses current project's resource planning data)
  const getPhaseBudgetedHours = useCallback((phase) => {
    const result = { total: 0, byPerson: {} };

    if (!phase.startDate || !phase.endDate) return result;

    const phaseStart = new Date(phase.startDate);
    const phaseEnd = new Date(phase.endDate);

    Object.entries(resourceData).forEach(([weekKey, weekData]) => {
      // Parse week key to get week number and year
      const match = weekKey.match(/KW(\d+)_(\d+)/);
      if (!match) return;

      const weekNum = parseInt(match[1]);
      const year = parseInt(match[2]);

      // Get the week's start date
      const weekStart = getWeekStartDate(weekNum, year);

      // Check if this week falls within the phase (week start is within phase dates)
      if (weekStart < phaseStart || weekStart > phaseEnd) return;

      Object.entries(weekData).forEach(([personId, hours]) => {
        result.total += hours;
        result.byPerson[personId] = (result.byPerson[personId] || 0) + hours;
      });
    });

    return result;
  }, [resourceData]);

  // Calculate FUTURE budgeted hours per phase per person (only weeks in the future)
  const getPhaseFutureBudgetedHours = useCallback((phase) => {
    const result = { total: 0, byPerson: {} };

    if (!phase.startDate || !phase.endDate) return result;

    const phaseStart = new Date(phase.startDate);
    const phaseEnd = new Date(phase.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    Object.entries(resourceData).forEach(([weekKey, weekData]) => {
      // Parse week key to get week number and year
      const match = weekKey.match(/KW(\d+)_(\d+)/);
      if (!match) return;

      const weekNum = parseInt(match[1]);
      const year = parseInt(match[2]);

      // Get the week's start date
      const weekStart = getWeekStartDate(weekNum, year);

      // Check if this week falls within the phase (week start is within phase dates)
      if (weekStart < phaseStart || weekStart > phaseEnd) return;

      // Only include weeks that are in the future (week start is after today)
      if (weekStart <= today) return;

      Object.entries(weekData).forEach(([personId, hours]) => {
        result.total += hours;
        result.byPerson[personId] = (result.byPerson[personId] || 0) + hours;
      });
    });

    return result;
  }, [resourceData]);

  // Calculate budget income/expense by phase from budget entries
  const getPhaseBudgetTotals = useCallback((phase) => {
    // Income (Einnahmen) - filter by typ instead of konto
    // Only include income entries that are explicitly assigned to this phase
    const incomeEntries = allBudgetEntries.filter(e => {
      if (e.typ !== "Einnahme") return false;
      // Only include entries with a phase that matches this phase (ignore entries without phase)
      if (!e.phase) return false;
      return e.phase === phase.name || e.phase === phase.id;
    });

    // Total income (all entries regardless of status)
    const incomeTotal = incomeEntries.reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    // Budgeted income (status is null/empty - not yet processed)
    const incomeBudgeted = incomeEntries
      .filter(e => !e.status || e.status === "" || e.status === "expected" || e.status === "expecting")
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    const incomePaid = incomeEntries
      .filter(e => e.status === "paid" || e.paid_date)
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    const incomeSent = incomeEntries
      .filter(e => e.status === "sent" || e.sent_date)
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    const incomeOverdue = incomeEntries
      .filter(e => e.status === "overdue")
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    // In Rechnung = sent + overdue
    const incomeInvoiced = incomeSent + incomeOverdue;

    // Expenses (Ausgaben) - filter by typ instead of konto
    const expenseEntries = allBudgetEntries.filter(e => {
      if (e.typ !== "Ausgabe") return false;
      return e.phase === phase.name || e.phase === phase.id;
    });

    // Total expenses (all entries regardless of status)
    const expenseTotal = expenseEntries.reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    // Budgeted expenses (status is null/empty - not yet processed)
    const expensesBudgeted = expenseEntries
      .filter(e => !e.status || e.status === "" || e.status === "expected" || e.status === "expecting")
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);

    const expensesPaid = expenseEntries
      .filter(e => e.status === "paid" || e.paid_date)
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    const expensesReceived = expenseEntries
      .filter(e => e.status === "received")
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    const expensesOverdue = expenseEntries
      .filter(e => e.status === "overdue")
      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
    // In Rechnung = received + overdue
    const expensesInvoiced = expensesReceived + expensesOverdue;

    return {
      incomeTotal,
      incomeBudgeted,
      incomePaid,
      incomeSent,
      incomeInvoiced,
      expenseTotal,
      expensesBudgeted,
      expensesPaid,
      expensesReceived,
      expensesInvoiced,
    };
  }, [allBudgetEntries]);

  // Calculate costs for a phase
  const getPhaseFinancials = useCallback((phase) => {
    const actualHours = getPhaseActualHours(phase);
    const budgetedHours = getPhaseBudgetedHours(phase);
    const futureBudgetedHours = getPhaseFutureBudgetedHours(phase);
    const budgetTotals = getPhaseBudgetTotals(phase);

    let internalCost = 0;
    let externalCost = 0;
    let internalCostPlanned = 0;
    let externalCostPlanned = 0;
    let internalCostPlannedFuture = 0;
    let externalCostPlannedFuture = 0;
    const personDetails = [];

    // Get all unique persons from both actual and budgeted hours
    const allPersonIds = new Set([
      ...Object.keys(actualHours.byPerson),
      ...Object.keys(budgetedHours.byPerson),
    ]);

    // Calculate costs per person
    allPersonIds.forEach(personId => {
      const hoursWorked = actualHours.byPerson[personId] || 0;
      const hoursBudgeted = budgetedHours.byPerson[personId] || 0;
      const hoursFutureBudgeted = futureBudgetedHours.byPerson[personId] || 0;
      const internalRate = getInternalRate(personId);
      const externalRate = personRates[personId] || 0;

      const personInternalCost = hoursWorked * internalRate;
      const personExternalCost = hoursWorked * externalRate;
      const personInternalCostPlanned = hoursBudgeted * internalRate;
      const personExternalCostPlanned = hoursBudgeted * externalRate;
      const personInternalCostPlannedFuture = hoursFutureBudgeted * internalRate;
      const personExternalCostPlannedFuture = hoursFutureBudgeted * externalRate;

      internalCost += personInternalCost;
      externalCost += personExternalCost;
      internalCostPlanned += personInternalCostPlanned;
      externalCostPlanned += personExternalCostPlanned;
      internalCostPlannedFuture += personInternalCostPlannedFuture;
      externalCostPlannedFuture += personExternalCostPlannedFuture;

      if (hoursWorked > 0 || hoursBudgeted > 0) {
        personDetails.push({
          personId,
          name: getEmployeeName(personId),
          hoursWorked,
          hoursBudgeted,
          hoursFutureBudgeted,
          hoursDiff: hoursWorked - hoursBudgeted,
          internalRate,
          externalRate,
          internalCost: personInternalCost,
          externalCost: personExternalCost,
          internalCostPlanned: personInternalCostPlanned,
          externalCostPlanned: personExternalCostPlanned,
        });
      }
    });

    // Calculate partner details from expense entries for this phase
    const expenseEntries = allBudgetEntries.filter(e => {
      if (e.typ !== "Ausgabe") return false;
      return e.phase === phase.name || e.phase === phase.id;
    });

    // Group expenses by partner
    const partnerMap = {};
    expenseEntries.forEach(entry => {
      const partnerId = entry.partner_id || "other";
      const partnerName = entry.partner_name || "Sonstige";

      if (!partnerMap[partnerId]) {
        partnerMap[partnerId] = {
          partnerId,
          partnerName,
          entries: [],
          totalAll: 0,        // Sum of all entries
          totalPaid: 0,       // Status "paid"
          totalInvoiced: 0,   // Status "expecting" or "entered_payment" (In Rechnung)
          totalBudgeted: 0,   // Status "expected" (Budgetiert)
        };
      }

      const amount = parseFloat(entry.netto_betrag || entry.betrag) || 0;
      partnerMap[partnerId].entries.push(entry);
      partnerMap[partnerId].totalAll += amount;

      // Categorize by status
      if (entry.status === "paid" || entry.paid_date) {
        // Bezahlt
        partnerMap[partnerId].totalPaid += amount;
      } else if (entry.status === "expecting" || entry.status === "entered_payment") {
        // In Rechnung
        partnerMap[partnerId].totalInvoiced += amount;
      } else if (entry.status === "expected") {
        // Budgetiert
        partnerMap[partnerId].totalBudgeted += amount;
      }
    });

    // Convert to array and sort (put "other" at end)
    const partnerDetails = Object.values(partnerMap).sort((a, b) => {
      if (a.partnerId === "other") return 1;
      if (b.partnerId === "other") return -1;
      return a.partnerName.localeCompare(b.partnerName);
    });

    return {
      hoursWorked: actualHours.total,
      hoursBudgeted: budgetedHours.total,
      hoursFutureBudgeted: futureBudgetedHours.total,
      // Adjusted plan: verarbeitet + future budgeted (this is the new "geplant" value)
      hoursAdjustedPlan: actualHours.total + futureBudgetedHours.total,
      hoursDiff: actualHours.total - budgetedHours.total,
      budget: parseFloat(phase.budget) || 0,
      externalBudget: parseFloat(phase.externalBudget) || 0,
      internalCost,
      externalCost,
      internalCostPlanned,
      externalCostPlanned,
      internalCostPlannedFuture,
      externalCostPlannedFuture,
      personDetails,
      partnerDetails,
      // Budget totals from budget entries
      incomeTotal: budgetTotals.incomeTotal,
      incomeBudgeted: budgetTotals.incomeBudgeted,
      incomePaid: budgetTotals.incomePaid,
      incomeSent: budgetTotals.incomeSent,
      incomeInvoiced: budgetTotals.incomeInvoiced,
      expenseTotal: budgetTotals.expenseTotal,
      expensesBudgeted: budgetTotals.expensesBudgeted,
      expensesPaid: budgetTotals.expensesPaid,
      expensesReceived: budgetTotals.expensesReceived,
      expensesInvoiced: budgetTotals.expensesInvoiced,
    };
  }, [getPhaseActualHours, getPhaseBudgetedHours, getPhaseFutureBudgetedHours, getPhaseBudgetTotals, getInternalRate, personRates, getEmployeeName, allBudgetEntries]);

  // Toggle finance phase expansion
  const toggleFinancePhase = (phaseId) => {
    setExpandedFinancePhases(prev =>
      prev.includes(phaseId)
        ? prev.filter(id => id !== phaseId)
        : [...prev, phaseId]
    );
  };

  // Get budget entries for a specific week (Einnahmen)
  const getEinnahmenForWeek = useCallback((weekNumber, year) => {
    const weekStart = getWeekStartDate(weekNumber, year);
    const weekEnd = getWeekEndDate(weekNumber, year);

    return allBudgetEntries.filter(entry => {
      if (entry.typ !== "Einnahme") return false;

      // For one-time entries, check if versand_datum falls in this week
      // Weekend dates are assigned to the previous week (containing Mon-Fri)
      if (entry.typ_timing === "Einmalig") {
        const dateStr = entry.versand_datum || entry.wann;
        if (!dateStr) return false;

        // Use effective week calculation (handles weekends)
        const effectiveWeek = getEffectiveWeekForDate(dateStr);
        if (!effectiveWeek) return false;

        return effectiveWeek.weekNum === weekNumber && effectiveWeek.year === year;
      }

      // For recurring entries, check if any occurrence falls in this week
      if (entry.typ_timing === "Regelmässig") {
        if (!entry.ab_wann) return false;
        const startDate = parseLocalDate(entry.ab_wann);
        const endDate = entry.bis_wann ? parseLocalDate(entry.bis_wann) : new Date("2099-12-31");

        // Simple check: does the recurring period overlap with this week?
        if (startDate > weekEnd || endDate < weekStart) return false;

        // More precise: check if there's an occurrence in this week based on frequency
        // For now, show if week is within range
        return true;
      }

      return false;
    });
  }, [allBudgetEntries]);

  // Get budget entries for a specific week (Drittkosten/Ausgaben)
  const getDrittkostenForWeek = useCallback((weekNumber, year) => {
    const weekStart = getWeekStartDate(weekNumber, year);
    const weekEnd = getWeekEndDate(weekNumber, year);

    const result = allBudgetEntries.filter(entry => {
      if (entry.typ !== "Ausgabe") return false;

      // For one-time entries, check if versand_datum falls in this week
      // Weekend dates are assigned to the previous week (containing Mon-Fri)
      if (entry.typ_timing === "Einmalig") {
        const dateStr = entry.versand_datum || entry.wann;
        if (!dateStr) {
          return false;
        }

        // Use effective week calculation (handles weekends)
        const effectiveWeek = getEffectiveWeekForDate(dateStr);
        if (!effectiveWeek) {
          return false;
        }

        return effectiveWeek.weekNum === weekNumber && effectiveWeek.year === year;
      }

      // For recurring entries, check if any occurrence falls in this week
      if (entry.typ_timing === "Regelmässig") {
        if (!entry.ab_wann) {
          return false;
        }
        const startDate = parseLocalDate(entry.ab_wann);
        const endDate = entry.bis_wann ? parseLocalDate(entry.bis_wann) : new Date("2099-12-31");

        // Simple check: does the recurring period overlap with this week?
        if (startDate > weekEnd || endDate < weekStart) {
          return false;
        }

        return true;
      }

      return false;
    });

    return result;
  }, [allBudgetEntries]);

  // Calculate total for week entries
  const calculateWeekTotal = useCallback((entries) => {
    return entries.reduce((sum, entry) => {
      return sum + (entry.brutto_betrag || entry.betrag || 0);
    }, 0);
  }, []);

  // Group expenses by partner for a specific week
  const groupExpensesByPartner = useCallback((weekNumber, year) => {
    const entries = getDrittkostenForWeek(weekNumber, year);
    const grouped = {};

    entries.forEach(entry => {
      const partnerId = entry.partner_id || "others";
      const partnerName = entry.partner_name || "Sonstige";

      if (!grouped[partnerId]) {
        grouped[partnerId] = {
          partnerId,
          partnerName: partnerId === "others" ? "Sonstige" : partnerName,
          entries: [],
          total: 0,
        };
      }
      grouped[partnerId].entries.push(entry);
      grouped[partnerId].total += (entry.brutto_betrag || entry.betrag || 0);
    });

    // Sort: put "others" at the end
    const sortedGroups = Object.values(grouped).sort((a, b) => {
      if (a.partnerId === "others") return 1;
      if (b.partnerId === "others") return -1;
      return a.partnerName.localeCompare(b.partnerName);
    });

    return sortedGroups;
  }, [getDrittkostenForWeek]);

  // Handle click on a Zahlungsplan cell
  const handleZahlungsplanCellClick = (weekNumber, year, typ, existingEntry = null) => {
    // Block creating new entries in readOnly mode
    if (!existingEntry && readOnly) return;

    const weekStart = getWeekStartDate(weekNumber, year);

    if (existingEntry) {
      // Edit existing entry (view-only in readOnly mode)
      setBudgetModalPreset({
        id: existingEntry.id,
      });
      setShowBudgetModal(true);
    } else {
      // Find phase for this week
      const phaseForWeek = phases.find(p => isPhaseInWeek(p, weekNumber, year));
      const phaseName = phaseForWeek?.name || "...";
      const phaseId = phaseForWeek?.id || phaseForWeek?.name || "";

      // versand_datum = Friday of this week (primary date for Zahlungsplan positioning)
      const versandDate = new Date(weekStart);
      versandDate.setDate(versandDate.getDate() + 4); // Monday + 4 = Friday

      setBudgetModalPreset({
        typ: typ,
        projekt: projectCode,
        versand_datum: versandDate.toISOString().split("T")[0],
        wann: versandDate.toISOString().split("T")[0], // backward compat
        typ_timing: "Einmalig",
        konto: typ === "Ausgabe" ? "4400" : "", // Preselect 4400 for expenses
        name: `${projectCode} - ${phaseName}`,
        phase: phaseId,
      });
      setShowBudgetModal(true);
    }
  };

  // Handle click on Drittkosten row to toggle expansion
  const handleDrittkostenRowClick = (weekNumber, year) => {
    const weekKey = `${year}-${weekNumber}`;
    const entries = getDrittkostenForWeek(weekNumber, year);

    if (entries.length > 0) {
      // Toggle expansion
      setExpandedDrittkostenWeek(prev =>
        prev === weekKey ? null : weekKey
      );
    } else if (!readOnly) {
      // No entries - open modal to create (only if not readOnly)
      handleZahlungsplanCellClick(weekNumber, year, "Ausgabe", null);
    }
  };

  // Handle click on a partner expense entry
  const handlePartnerExpenseClick = (e, entry) => {
    e.stopPropagation();
    setBudgetModalPreset({ id: entry.id });
    setShowBudgetModal(true);
  };

  // Handle click to add new expense for a partner
  const handleAddPartnerExpense = (e, weekNumber, year, partnerId, partnerName) => {
    e.stopPropagation();
    if (readOnly) return; // Block creating new entries in readOnly mode

    const weekStart = getWeekStartDate(weekNumber, year);

    // Find phase for this week
    const phaseForWeek = phases.find(p => isPhaseInWeek(p, weekNumber, year));
    const phaseId = phaseForWeek?.id || phaseForWeek?.name || "";

    // versand_datum = Friday of this week (primary date for Zahlungsplan positioning)
    const versandDate = new Date(weekStart);
    versandDate.setDate(versandDate.getDate() + 4); // Monday + 4 = Friday

    // Name format: "{project-code} - {partner}" when partner is selected
    const entryName = partnerId !== "others" && partnerName
      ? `${projectCode} - ${partnerName}`
      : `${projectCode} - ...`;

    setBudgetModalPreset({
      typ: "Ausgabe",
      projekt: projectCode,
      versand_datum: versandDate.toISOString().split("T")[0],
      wann: versandDate.toISOString().split("T")[0], // backward compat
      typ_timing: "Einmalig",
      konto: "4400", // Preselect 4400 for expenses
      name: entryName,
      phase: phaseId,
      partner_id: partnerId !== "others" ? partnerId : "",
      partner_name: partnerId !== "others" ? partnerName : "",
    });
    setShowBudgetModal(true);
  };

  // Handle budget modal close and refresh data
  const handleBudgetModalClose = async () => {
    setShowBudgetModal(false);
    setBudgetModalPreset(null);

    // Reload budget data
    try {
      const projectBudgets = await getBudgetEntriesForProject(projectCode);
      setBudgetData(projectBudgets);
      setAllBudgetEntries(projectBudgets);
    } catch (err) {
      console.error("Error reloading budget data:", err);
    }
  };

  // Handle click on person name in Finanzen table
  const handleFinancePersonClick = (e, personId) => {
    e.stopPropagation();
    const currentAssignment = projectAssignments.find(a => a.employeeUuid === personId);
    setSelectedFinancePerson({
      personId,
      name: getEmployeeName(personId),
      productUuid: currentAssignment?.productUuid || null,
    });
    setShowExternalRateModal(true);
  };

  // Save external rate by updating assignment with product
  const handleSaveExternalRate = async () => {
    if (!selectedFinancePerson) return;

    try {
      const { personId, productUuid } = selectedFinancePerson;

      // Update or create the assignment
      const existingAssignments = [...projectAssignments];
      const existingIndex = existingAssignments.findIndex(a => a.employeeUuid === personId);

      if (existingIndex >= 0) {
        existingAssignments[existingIndex] = {
          ...existingAssignments[existingIndex],
          productUuid,
        };
      } else {
        existingAssignments.push({
          employeeUuid: personId,
          productUuid,
          role: "",
        });
      }

      await saveProjectAssignments(projectCode, existingAssignments);
      setProjectAssignments(existingAssignments);

      // Update person rates
      if (productUuid) {
        const product = products.find(p => p.uuid === productUuid);
        if (product) {
          setPersonRates(prev => ({
            ...prev,
            [personId]: product.cost || 0,
          }));
        }
      } else {
        setPersonRates(prev => {
          const newRates = { ...prev };
          delete newRates[personId];
          return newRates;
        });
      }

      setShowExternalRateModal(false);
      setSelectedFinancePerson(null);
    } catch (err) {
      console.error("Error saving external rate:", err);
      setError("Fehler beim Speichern des Stundensatzes");
    }
  };

  // Handle click on person name in Finanzen table (within a phase) to edit their product for that phase
  const handlePhasePersonProductClick = (e, phase, personId, personName) => {
    e.stopPropagation();
    const currentProductUuid = phase.personProducts?.[personId] || "";
    setSelectedPhaseForRate({
      phase,
      personId,
      personName,
      productUuid: currentProductUuid,
    });
    setShowPhaseRateModal(true);
  };

  // Save person's product for a specific phase
  const handleSavePhaseProduct = async () => {
    if (!selectedPhaseForRate) return;

    try {
      const { phase, personId, productUuid } = selectedPhaseForRate;
      const newPersonProducts = { ...(phase.personProducts || {}) };

      if (productUuid) {
        newPersonProducts[personId] = productUuid;
      } else {
        delete newPersonProducts[personId];
      }

      const updatedPhases = phases.map(p =>
        p.id === phase.id
          ? { ...p, personProducts: newPersonProducts }
          : p
      );

      await saveBulkPhases(projectCode, updatedPhases);
      setPhases(updatedPhases);
      setShowPhaseRateModal(false);
      setSelectedPhaseForRate(null);
    } catch (err) {
      console.error("Error saving phase person product:", err);
      setError("Fehler beim Speichern der Leistung");
    }
  };

  // Helper to get person's product for a specific phase
  const getPhasePersonProduct = (phase, personId) => {
    return phase.personProducts?.[personId] || null;
  };

  // Helper to get product info by uuid
  const getProductInfo = (productUuid) => {
    return products.find(p => p.uuid === productUuid);
  };

  const handleNavigate = (direction) => {
    setStartWeekOffset((prev) => prev + direction * 4);
  };

  const handleToday = () => {
    setStartWeekOffset(0);
    setTimeout(() => {
      currentWeekRef.current?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }, 100);
  };

  const handlePersonClick = (personId) => {
    setExpandedPerson((prev) => (prev === personId ? null : personId));
  };

  // Handle person name click to open rate modal
  const handlePersonNameClick = (e, personId) => {
    e.stopPropagation();
    setSelectedPersonForRate(personId);
    setShowRateModal(true);
  };

  // Save person rate (now uses the new projectRates table)
  const handleSaveRate = async (personId, rate) => {
    try {
      const numericRate = parseFloat(rate) || 0;

      // Save to new projectRates table (default = project-level rate)
      await saveProjectRate(projectCode, personId, "default", numericRate);

      // Update local state
      setPersonRates((prev) => ({
        ...prev,
        [personId]: numericRate,
      }));

      setShowRateModal(false);
      setSelectedPersonForRate(null);
    } catch (err) {
      console.error("Error saving rate:", err);
      setError("Fehler beim Speichern des Stundensatzes");
    }
  };

  // Handle hours change
  const handlePersonHoursChange = (weekNumber, year, personUuid, value) => {
    const hours = parseFloat(value) || 0;
    const weekKey = `KW${weekNumber}_${year}`;

    setResourceData((prev) => {
      const newData = { ...prev };
      if (!newData[weekKey]) {
        newData[weekKey] = {};
      }
      newData[weekKey][personUuid] = hours;
      return newData;
    });

    // Debounced save - direct Azure Table Storage update
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const rowKey = `${projectCode}_${personUuid}_${weekNumber}_${year}`;
        const now = new Date().toISOString();
        const url = `https://mfprojects.table.core.windows.net/resourcePlanning(PartitionKey='resource',RowKey='${rowKey}')?sv=2024-11-04&ss=t&srt=sco&sp=rwdlacu&se=2035-07-04T17:08:25Z&st=2025-07-04T09:08:25Z&spr=https&sig=X7s%2BFIyYcKvRzSxRNAp5BUJEe2YUcg8nojkn9fIfcTY%3D`;

        const entity = {
          PartitionKey: "resource",
          RowKey: rowKey,
          ProjectNumber: projectCode,
          EmployeeUuid: personUuid,
          WeekNumber: weekNumber,
          Year: year,
          Hours: hours,
          CreatedAt: now,
          UpdatedAt: now,
        };

        await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json;odata=nometadata",
          },
          body: JSON.stringify(entity),
        });
      } catch (err) {
        console.error("Speichern fehlgeschlagen:", err);
        setError("Fehler beim Speichern");
      }
    }, 500);
  };

  // Phase modal handlers - now opens for adding new phase
  const handleAddPhaseClick = () => {
    setSelectedPhase({
      id: null,
      name: "",
      startDate: "",
      endDate: "",
      budget: "",
      externalBudget: "",
    });
    setShowPhaseModal(true);
  };

  // Click on phase bar to edit
  const handlePhaseClick = (phase) => {
    setSelectedPhase(phase);
    setShowPhaseModal(true);
  };

  const handleSavePhase = async () => {
    try {
      let updatedPhases;
      let oldPhaseName = null;

      if (selectedPhase.id) {
        // Detect phase rename
        const existingPhase = phases.find((p) => p.id === selectedPhase.id);
        if (existingPhase && existingPhase.name !== selectedPhase.name) {
          oldPhaseName = existingPhase.name;
        }

        updatedPhases = phases.map((p) =>
          p.id === selectedPhase.id ? selectedPhase : p
        );
      } else {
        updatedPhases = [...phases, { ...selectedPhase, id: Date.now().toString() }];
      }

      await saveBulkPhases(projectCode, updatedPhases);

      // Cascade phase rename to budget entries
      if (oldPhaseName) {
        const allEntries = await getBudgetEntriesForProject(projectCode);
        const entriesToUpdate = allEntries.filter(
          (e) => e.phase === oldPhaseName
        );
        if (entriesToUpdate.length > 0) {
          await Promise.all(
            entriesToUpdate.map((e) =>
              saveBudgetEntry({ ...e, phase: selectedPhase.name })
            )
          );
        }
      }

      setPhases(updatedPhases);
      setShowPhaseModal(false);
      setSelectedPhase(null);
      setPhaseClickWeek(null);
    } catch (err) {
      console.error("Error saving phase:", err);
      setError("Fehler beim Speichern der Phase");
    }
  };

  const handleDeletePhase = async () => {
    if (!selectedPhase?.id) return;
    try {
      await deletePhase(projectCode, selectedPhase.id);
      setPhases(phases.filter((p) => p.id !== selectedPhase.id));
      setShowPhaseModal(false);
      setSelectedPhase(null);
    } catch (err) {
      console.error("Error deleting phase:", err);
      setError("Fehler beim Löschen der Phase");
    }
  };

  // Projekt-Level localOnly Toggle
  const handleToggleProjectLocalOnly = async () => {
    if (!project || !userCanSetLocalOnly) return;
    try {
      const updatedProject = { ...project, localOnly: !project.localOnly };
      await ProjectsService.updateProject(project.id, updatedProject);
      setProject(updatedProject);
    } catch (err) {
      console.error("Error toggling project localOnly:", err);
      setError("Fehler beim Ändern des localOnly-Flags");
    }
  };

  // Update partner budget for a specific phase
  const handlePartnerBudgetChange = async (phaseId, partnerId, newBudget) => {
    try {
      const phase = phases.find(p => p.id === phaseId);
      if (!phase) return;

      // Parse the budget value
      const budgetValue = parseFloat(newBudget) || 0;

      // Update or create partnerBudgets object
      const updatedPartnerBudgets = {
        ...(phase.partnerBudgets || {}),
        [partnerId]: budgetValue,
      };

      // Update the phase
      const updatedPhase = {
        ...phase,
        partnerBudgets: updatedPartnerBudgets,
      };

      const updatedPhases = phases.map(p => p.id === phaseId ? updatedPhase : p);

      // Save to backend
      await saveBulkPhases(projectCode, updatedPhases);
      setPhases(updatedPhases);
      setEditingPartnerBudget(null);
    } catch (err) {
      console.error("Error saving partner budget:", err);
      setError("Fehler beim Speichern des Partner-Budgets");
    }
  };

  // Get partner budget from phase
  const getPartnerBudget = (phase, partnerId) => {
    return phase?.partnerBudgets?.[partnerId] || 0;
  };

  // Handle clicking on a phase cell to add/edit milestone
  const handleMilestoneClick = (e, phaseId, weekNumber, year) => {
    e.stopPropagation();
    const phase = phases.find(p => p.id === phaseId);
    const existingMilestone = phase?.milestones?.find(
      m => m.weekNumber === weekNumber && m.year === year
    );
    setMilestoneInputValue(existingMilestone?.name || "");
    setEditingMilestone({ phaseId, weekNumber, year });
  };

  // Save milestone
  const handleMilestoneSave = async () => {
    if (!editingMilestone) return;

    try {
      const { phaseId, weekNumber, year } = editingMilestone;
      const phase = phases.find(p => p.id === phaseId);
      if (!phase) return;

      // Get existing milestones or initialize empty array
      let milestones = [...(phase.milestones || [])];

      // Find existing milestone for this week
      const existingIdx = milestones.findIndex(
        m => m.weekNumber === weekNumber && m.year === year
      );

      if (milestoneInputValue.trim()) {
        // Add or update milestone
        const milestone = { weekNumber, year, name: milestoneInputValue.trim() };
        if (existingIdx >= 0) {
          milestones[existingIdx] = milestone;
        } else {
          milestones.push(milestone);
        }
      } else {
        // Remove milestone if name is empty
        if (existingIdx >= 0) {
          milestones.splice(existingIdx, 1);
        }
      }

      // Update the phase
      const updatedPhase = { ...phase, milestones };
      const updatedPhases = phases.map(p => p.id === phaseId ? updatedPhase : p);

      // Save to backend
      await saveBulkPhases(projectCode, updatedPhases);
      setPhases(updatedPhases);
      setEditingMilestone(null);
      setMilestoneInputValue("");
    } catch (err) {
      console.error("Error saving milestone:", err);
      setError("Fehler beim Speichern des Meilensteins");
    }
  };

  // Get milestone for a specific week in a phase
  const getPhaseMilestone = (phase, weekNumber, year) => {
    return phase?.milestones?.find(
      m => m.weekNumber === weekNumber && m.year === year
    );
  };

  const handleProjectChange = (e) => {
    const newProjectCode = e.target.value;
    if (isInline && onProjectChange) {
      const newProject = allProjects.find((p) => p.code === newProjectCode);
      if (newProject) {
        onProjectChange(newProject);
      }
    } else {
      navigate(`/controlling/${newProjectCode}`);
    }
  };

  // Toggle all person rows visibility
  const handleTotalRowClick = () => {
    setShowPersonRows((prev) => !prev);
  };

  const formatWeekDate = (date) => {
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const MAX_BAR_HEIGHT = 200;
  const PERSON_ROW_HEIGHT = 100;

  if (loading) {
    return (
      <div className="controlling">
        <div className="container">
          <div className="loading-state">Lade Daten...</div>
        </div>
      </div>
    );
  }

  // Calculate max values for scaling
  let maxTotalHours = 0;
  weeks.forEach((week) => {
    const planned = getTotalPlannedForWeek(week.number, week.year);
    const actual = getTotalActualForWeek(week.number, week.year);
    maxTotalHours = Math.max(maxTotalHours, planned, actual);
  });

  const getTotalBarHeight = (hours) => {
    if (maxTotalHours === 0) return 0;
    return (hours / maxTotalHours) * MAX_BAR_HEIGHT;
  };

  return (
    <div className="controlling">
      <div className="container">
        <div className="controlling-header">
          <h1>Controlling</h1>
          <select
            value={projectCode}
            onChange={handleProjectChange}
            className="project-selector"
          >
            {allProjects
              .filter((p) => p.type === "project" || !p.type)
              .sort((a, b) => b.code.localeCompare(a.code))
              .map((p) => (
                <option key={p.code} value={p.code}>
                  {p.code} - {p.title}
                </option>
              ))}
          </select>
          <button
            className="btn zahlungsplan-btn"
            onClick={() => setShowZahlungsplanModal(true)}
            title="Zahlungsplan verwalten"
          >
            Zahlungsplan
          </button>
          <button
            className="btn zahlungsplan-btn"
            onClick={() => setShowAnsaetzeModal(true)}
            title="Stundensätze verwalten"
          >
            Ansätze
          </button>
          <button
            className="btn zahlungsplan-btn"
            onClick={() => setShowAssignmentModal(true)}
            disabled={readOnly}
            title="Personen-Zuordnung verwalten"
          >
            Zuordnung
          </button>
          <button
            className="btn zahlungsplan-btn"
            onClick={() => setShowFirmenModal(true)}
            disabled={readOnly}
            title="Firmen (Auftraggeberin/Partner/Consultant/Contractor) verwalten"
          >
            Firmen
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Filter-Leiste */}
        <div className="filter-bar">
          <div className="navigation-controls">
            <button className="nav-btn" onClick={() => handleNavigate(-1)}>
              Zurück
            </button>
            <button className="today-btn" onClick={handleToday}>
              Heute
            </button>
            <button className="nav-btn" onClick={() => handleNavigate(1)}>
              Vor
            </button>
          </div>

          <div className="project-info">
            <span className="project-title">{project?.title}</span>
          </div>
        </div>

        {/* Timeline */}
        <div className="timeline-wrapper" ref={timelineWrapperRef}>
          <div className="timeline-content">
            {/* KW Header */}
            <div className="timeline-header">
              <div className="label-column header-label"></div>
              {weeks.map((week, idx) => {
                const isNewYear = idx > 0 && weeks[idx - 1].year !== week.year;
                return (
                  <div
                    key={`${week.number}-${week.year}`}
                    className={`week-column ${
                      week.isCurrentWeek ? "current-week" : ""
                    }`}
                    ref={week.isCurrentWeek ? currentWeekRef : null}
                  >
                    {(idx === 0 || isNewYear) && (
                      <div className="year-label">{week.year}</div>
                    )}
                    <div className="week-number">{week.number}</div>
                    <div className="week-date">
                      {formatWeekDate(week.startDate)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Waterfall Timeline - Each Phase as its own row */}
            {phases.map((phase, phaseIndex) => {
              const phaseColor = phase.color || projectColor;

              return (
                <div key={phase.id || phaseIndex} className="phase-row">
                  <div
                    className="label-column phase-label"
                    onClick={() => handlePhaseClick(phase)}
                    title="Klicken zum Bearbeiten"
                  >
                    <span className="phase-name-text">{phase.name || "Unbenannt"}</span>
                  </div>
                  {weeks.map((week) => {
                    const isInPhase = isPhaseInWeek(phase, week.number, week.year);
                    const isStart = isInPhase && (() => {
                      const prevIdx = weeks.findIndex(
                        (w) => w.number === week.number && w.year === week.year
                      ) - 1;
                      if (prevIdx < 0) return true;
                      const prevWeek = weeks[prevIdx];
                      return !isPhaseInWeek(phase, prevWeek.number, prevWeek.year);
                    })();
                    const isEnd = isInPhase && (() => {
                      const currIdx = weeks.findIndex(
                        (w) => w.number === week.number && w.year === week.year
                      );
                      const nextIdx = currIdx + 1;
                      if (nextIdx >= weeks.length) return true;
                      const nextWeek = weeks[nextIdx];
                      return !isPhaseInWeek(phase, nextWeek.number, nextWeek.year);
                    })();

                    const milestone = getPhaseMilestone(phase, week.number, week.year);
                    const isEditingThisMilestone = editingMilestone?.phaseId === phase.id &&
                      editingMilestone?.weekNumber === week.number &&
                      editingMilestone?.year === week.year;

                    return (
                      <div
                        key={`phase-${phase.id}-${week.number}-${week.year}`}
                        className={`week-column phase-cell ${isInPhase ? "has-phase" : ""}`}
                      >
                        {isInPhase && (
                          <div
                            className={`phase-bar-segment ${isStart ? "start" : ""} ${isEnd ? "end" : ""}`}
                            style={{ backgroundColor: phaseColor }}
                            onClick={(e) => handleMilestoneClick(e, phase.id, week.number, week.year)}
                            title={milestone ? milestone.name : "Klicken um Meilenstein hinzuzufügen"}
                          >
                            {/* Milestone rhombus */}
                            {milestone && (
                              <svg className="milestone-icon" viewBox="0 0 16 16" width="14" height="14">
                                <polygon points="8,1 15,8 8,15 1,8" fill="#fff" stroke={phaseColor} strokeWidth="1.5" />
                              </svg>
                            )}
                            {/* Milestone edit popup */}
                            {isEditingThisMilestone && (
                              <div className="milestone-popup" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="text"
                                  className="milestone-input"
                                  value={milestoneInputValue}
                                  onChange={(e) => setMilestoneInputValue(e.target.value)}
                                  placeholder="Meilenstein-Name"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleMilestoneSave();
                                    if (e.key === 'Escape') {
                                      setEditingMilestone(null);
                                      setMilestoneInputValue("");
                                    }
                                  }}
                                />
                                <div className="milestone-actions">
                                  <button className="milestone-save" onClick={handleMilestoneSave}>✓</button>
                                  <button className="milestone-cancel" onClick={() => {
                                    setEditingMilestone(null);
                                    setMilestoneInputValue("");
                                  }}>✕</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {/* Add Phase Row */}
            {!readOnly && (
              <div className="add-phase-row">
                <div className="label-column">
                  <button className="add-phase-btn" onClick={handleAddPhaseClick}>
                    + Phase hinzufügen
                  </button>
                </div>
              </div>
            )}

            {/* Total Row */}
            {(() => {
              const totalActualPoints = [];

              weeks.forEach((week, idx) => {
                const isPast = isWeekInPast(week.number, week.year);
                if (isPast) {
                  const totalActualHours = getTotalActualForWeek(week.number, week.year);
                  if (totalActualHours > 0) {
                    const height = getTotalBarHeight(totalActualHours);
                    totalActualPoints.push({
                      idx,
                      x: idx * 36 + 18,
                      y: MAX_BAR_HEIGHT - height,
                      hours: totalActualHours,
                    });
                  }
                }
              });

              const totalPolylinePoints = totalActualPoints
                .map((p) => `${p.x},${p.y}`)
                .join(" ");

              return (
                <div
                  className={`total-row ${showPersonRows ? "expanded" : "collapsed"}`}
                  onClick={handleTotalRowClick}
                  title={showPersonRows ? "Klicken zum Zuklappen" : "Klicken zum Aufklappen"}
                >
                  <div className="label-column total-label">
                    <span className="toggle-icon">{showPersonRows ? "▼" : "▶"}</span>
                    <span>Total</span>
                  </div>

                  {/* SVG overlay for actual hours line */}
                  {totalActualPoints.length > 1 && (
                    <svg
                      className="total-lines-overlay"
                      style={{
                        position: "absolute",
                        left: "140px",
                        bottom: "22px",
                        width: `${weeks.length * 36}px`,
                        height: `${MAX_BAR_HEIGHT}px`,
                        pointerEvents: "none",
                        zIndex: 15,
                      }}
                    >
                      <polyline
                        points={totalPolylinePoints}
                        fill="none"
                        stroke="white"
                        strokeWidth="2"
                      />
                      {totalActualPoints.map((p) => (
                        <circle
                          key={p.idx}
                          cx={p.x}
                          cy={p.y}
                          r="5"
                          fill="white"
                          stroke="white"
                          strokeWidth="2"
                        />
                      ))}
                    </svg>
                  )}

                  {weeks.map((week) => {
                    const isPast = isWeekInPast(week.number, week.year);

                    // Build stacked segments by person
                    const segments = projectPersons
                      .map((personId) => ({
                        personId,
                        hours: getPersonHoursForWeek(week.number, week.year, personId),
                        color: getPersonColor(personId),
                      }))
                      .filter((s) => s.hours > 0);

                    const totalPlanned = getTotalPlannedForWeek(week.number, week.year);

                    return (
                      <div
                        key={`total-${week.number}-${week.year}`}
                        className={`week-column total-cell ${isPast ? "past-week" : ""}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="stacked-bar-container">
                          <div className="stacked-bars">
                            {segments.map((segment) => {
                              const isHighlighted = hoveredPerson === segment.personId;
                              const isDimmed = hoveredPerson && hoveredPerson !== segment.personId;
                              return (
                                <div
                                  key={segment.personId}
                                  className={`bar-segment ${isHighlighted ? "highlighted" : ""} ${isDimmed ? "dimmed" : ""}`}
                                  style={{
                                    height: `${getTotalBarHeight(segment.hours)}px`,
                                    backgroundColor: segment.color,
                                  }}
                                  onMouseEnter={() => setHoveredPerson(segment.personId)}
                                  onMouseLeave={() => setHoveredPerson(null)}
                                  title={`${getEmployeeName(segment.personId)}: ${segment.hours}h${findTeamMember(teamMembersData, segment.personId) ? "" : ` (ID: ${segment.personId})`}`}
                                />
                              );
                            })}
                          </div>
                        </div>
                        <div className="total-hours">
                          {totalPlanned > 0 && `${Math.round(totalPlanned)}h`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Separator */}
            <div className="separator-row" />

            {/* Person Rows - only shown when showPersonRows is true */}
            {showPersonRows && (() => {
              // Calculate global max hours across ALL persons for consistent scaling
              let globalMaxPersonHours = 0;
              projectPersons.forEach((personId) => {
                weeks.forEach((week) => {
                  const planned = getPersonHoursForWeek(week.number, week.year, personId);
                  const actual = getActualPersonHoursForWeek(week.number, week.year, personId);
                  globalMaxPersonHours = Math.max(globalMaxPersonHours, planned, actual);
                });
              });

              // Shared function for calculating bar height using global max
              const getPersonBarHeight = (hours) => {
                if (globalMaxPersonHours === 0) return 0;
                return Math.max((hours / globalMaxPersonHours) * PERSON_ROW_HEIGHT, hours > 0 ? 8 : 0);
              };

              return projectPersons.map((personId) => {
              const isExpanded = expandedPerson === personId;
              const isHovered = hoveredPerson === personId;
              const isDimmed = hoveredPerson && !isHovered;
              const color = getPersonColor(personId);
              const personRate = personRates[personId] || 0;

              // Build actual hours points
              const actualHoursPoints = [];
              weeks.forEach((week, idx) => {
                const isPast = isWeekInPast(week.number, week.year);
                if (isPast) {
                  const actualHours = getActualPersonHoursForWeek(week.number, week.year, personId);
                  if (actualHours > 0) {
                    const height = getPersonBarHeight(actualHours);
                    actualHoursPoints.push({
                      idx,
                      x: idx * 36 + 18,
                      y: PERSON_ROW_HEIGHT - height + 10,
                      hours: actualHours,
                    });
                  }
                }
              });

              const polylinePoints = actualHoursPoints
                .map((p) => `${p.x},${p.y}`)
                .join(" ");

              return (
                <React.Fragment key={personId}>
                  <div
                    className={`person-row ${isHovered ? "highlighted" : ""} ${isDimmed ? "dimmed" : ""}`}
                    onClick={() => handlePersonClick(personId)}
                  >
                    <div className="label-column person-label">
                      <span
                        className="color-dot"
                        style={{ backgroundColor: color }}
                      />
                      <span
                        className={`person-name ${!readOnly ? 'clickable' : ''}`}
                        onClick={(e) => !readOnly && handlePersonNameClick(e, personId)}
                        title={!readOnly ? "Klicken um Stundensatz zu bearbeiten" : undefined}
                      >
                        {getEmployeeName(personId)}
                      </span>
                      {!readOnly && personRate > 0 && (
                        <span className="person-rate">{personRate} CHF/h</span>
                      )}
                    </div>

                    {/* SVG overlay for actual hours line */}
                    {actualHoursPoints.length > 1 && !isExpanded && (
                      <svg
                        className="actual-hours-overlay"
                        style={{
                          position: "absolute",
                          left: "140px",
                          bottom: "4px",
                          width: `${weeks.length * 36}px`,
                          height: `${PERSON_ROW_HEIGHT + 10}px`,
                          pointerEvents: "none",
                          zIndex: 15,
                        }}
                      >
                        <polyline
                          points={polylinePoints}
                          fill="none"
                          stroke="white"
                          strokeWidth="2"
                        />
                        {actualHoursPoints.map((p) => (
                          <circle
                            key={p.idx}
                            cx={p.x}
                            cy={p.y}
                            r="4"
                            fill="white"
                            stroke="white"
                            strokeWidth="2"
                          />
                        ))}
                      </svg>
                    )}

                    {weeks.map((week, idx) => {
                      const hours = getPersonHoursForWeek(week.number, week.year, personId);
                      const prevHours = idx > 0
                        ? getPersonHoursForWeek(weeks[idx - 1].number, weeks[idx - 1].year, personId)
                        : 0;
                      const nextHours = idx < weeks.length - 1
                        ? getPersonHoursForWeek(weeks[idx + 1].number, weeks[idx + 1].year, personId)
                        : 0;
                      const showLine = hours > 0 || prevHours > 0 || nextHours > 0;
                      const barHeight = getPersonBarHeight(hours);
                      const isPast = isWeekInPast(week.number, week.year);

                      return (
                        <div
                          key={`${personId}-${week.number}-${week.year}`}
                          className={`week-column person-cell ${showLine ? "has-activity" : ""} ${isPast ? "past-week" : ""}`}
                        >
                          {hours > 0 && (
                            <div
                              className="person-bar"
                              style={{
                                height: `${barHeight}px`,
                                backgroundColor: color,
                              }}
                            >
                              <span className="bar-hours">
                                {Math.round(hours)}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Expanded edit row */}
                  {isExpanded && (() => {
                    // Calculate remaining hours for hovered week
                    const isHoveredPerson = hoveredEditCell?.personId === personId;
                    const remainingHours = isHoveredPerson
                      ? getPersonRemainingHours(
                          personId,
                          hoveredEditCell.weekNumber,
                          hoveredEditCell.year
                        )
                      : null;

                    return (
                      <div className="edit-row">
                        <div className="label-column edit-label">
                          <span className="edit-text">Bearbeiten</span>
                          {isHoveredPerson && remainingHours !== null && (
                            <span className={`remaining-hours ${remainingHours < 0 ? "negative" : ""}`}>
                              {remainingHours >= 0 ? `+${Math.round(remainingHours)}h` : `${Math.round(remainingHours)}h`}
                            </span>
                          )}
                        </div>
                        {weeks.map((week) => {
                          const hours = getPersonHoursForWeek(week.number, week.year, personId);
                          const isPast = isWeekInPast(week.number, week.year);
                          const actualHours = isPast
                            ? getActualPersonHoursForWeek(week.number, week.year, personId)
                            : 0;

                          return (
                            <div
                              key={`edit-${personId}-${week.number}-${week.year}`}
                              className={`week-column edit-cell ${isPast ? "past-week" : ""}`}
                              onMouseEnter={() => !isPast && setHoveredEditCell({ personId, weekNumber: week.number, year: week.year })}
                              onMouseLeave={() => setHoveredEditCell(null)}
                            >
                              {isPast ? (
                                <div className="actual-display">
                                  <span className="actual-value">{actualHours > 0 ? `${Math.round(actualHours)}h` : "-"}</span>
                                </div>
                              ) : (
                                <input
                                  type="number"
                                  min="0"
                                  max="60"
                                  step="0.5"
                                  value={hours || ""}
                                  placeholder=""
                                  onChange={(e) =>
                                    handlePersonHoursChange(
                                      week.number,
                                      week.year,
                                      personId,
                                      e.target.value
                                    )
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.target.select()}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </React.Fragment>
              );
            });
            })()}

            {/* Zahlungsplan Section - within scroll container */}
            <div className="zahlungsplan-section">
              <div className="zahlungsplan-header">
                <div className="label-column zahlungsplan-title">Zahlungsplan</div>
              </div>

              {/* Einnahmen Row */}
              <div className="zahlungsplan-row einnahmen-row">
                <div className="label-column zahlungsplan-label">
                  <span className="zahlungsplan-row-icon">↓</span>
                  <span>Einnahmen</span>
                </div>
                {weeks.map((week) => {
                  const entries = getEinnahmenForWeek(week.number, week.year);
                  const total = calculateWeekTotal(entries);
                  const hasEntries = entries.length > 0;

                  return (
                    <div
                      key={`einnahmen-${week.number}-${week.year}`}
                      className={`week-column zahlungsplan-cell ${hasEntries ? "has-entry" : ""} ${week.isCurrentWeek ? "current-week" : ""}`}
                      title={hasEntries ? entries.map(e => `${e.name}: ${formatCurrency(e.brutto_betrag || e.betrag)}`).join("\n") : ""}
                    >
                      {hasEntries && (
                        <div className="zahlungsplan-entry einnahme">
                          <span className="entry-amount">
                            {entries.length > 1 ? `${entries.length}×` : ""}
                            {total >= 1000 ? `${Math.round(total / 1000)}k` : Math.round(total)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Drittkosten Row */}
              <div className="zahlungsplan-row drittkosten-row">
                <div className="label-column zahlungsplan-label">
                  <span className="zahlungsplan-row-icon">↑</span>
                  <span>Drittkosten</span>
                  {expandedDrittkostenWeek && <span className="expand-indicator">▼</span>}
                </div>
                {weeks.map((week) => {
                  const entries = getDrittkostenForWeek(week.number, week.year);
                  const total = calculateWeekTotal(entries);
                  const hasEntries = entries.length > 0;
                  const weekKey = `${week.year}-${week.number}`;
                  const isExpanded = expandedDrittkostenWeek === weekKey;

                  return (
                    <div
                      key={`drittkosten-${week.number}-${week.year}`}
                      className={`week-column zahlungsplan-cell ${hasEntries ? "has-entry" : ""} ${week.isCurrentWeek ? "current-week" : ""} ${isExpanded ? "expanded" : ""}`}
                      onClick={hasEntries ? () => {
                        const weekKey = `${week.year}-${week.number}`;
                        setExpandedDrittkostenWeek(prev => prev === weekKey ? null : weekKey);
                      } : undefined}
                      title={hasEntries ? `${entries.length} Position(en) - Klicken zum Aufklappen` : ""}
                    >
                      {hasEntries && (
                        <div className="zahlungsplan-entry ausgabe">
                          <span className="entry-amount">
                            {entries.length > 1 ? `${entries.length}×` : ""}
                            {total >= 1000 ? `${Math.round(total / 1000)}k` : Math.round(total)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Expanded Partner Rows for Drittkosten */}
              {expandedDrittkostenWeek && (() => {
                const [year, weekNum] = expandedDrittkostenWeek.split("-").map(Number);
                const partnerGroups = groupExpensesByPartner(weekNum, year);

                return partnerGroups.map((group, groupIndex) => (
                  <div key={`partner-${group.partnerId}`} className={`zahlungsplan-row partner-expense-row ${group.partnerId === "others" ? "others-row" : ""}`}>
                    <div className="label-column zahlungsplan-label partner-label">
                      <span className="partner-indent">└</span>
                      <span className="partner-name-text">{group.partnerName}</span>
                    </div>
                    {weeks.map((week) => {
                      const weekKey = `${week.year}-${week.number}`;
                      const isTargetWeek = weekKey === expandedDrittkostenWeek;

                      if (!isTargetWeek) {
                        return (
                          <div
                            key={`partner-${group.partnerId}-${week.number}-${week.year}`}
                            className="week-column zahlungsplan-cell partner-cell empty"
                          />
                        );
                      }

                      return (
                        <div
                          key={`partner-${group.partnerId}-${week.number}-${week.year}`}
                          className="week-column zahlungsplan-cell partner-cell has-data"
                        >
                          {group.entries.map((entry, idx) => (
                            <div
                              key={entry.id}
                              className="partner-expense-entry"
                              title={`${entry.name}: ${formatCurrency(entry.brutto_betrag || entry.betrag)}`}
                            >
                              <span className="expense-name">{entry.name?.substring(0, 10)}{entry.name?.length > 10 ? "..." : ""}</span>
                              <span className="expense-amount">
                                {(entry.brutto_betrag || entry.betrag) >= 1000
                                  ? `${Math.round((entry.brutto_betrag || entry.betrag) / 1000)}k`
                                  : Math.round(entry.brutto_betrag || entry.betrag)}
                              </span>
                            </div>
                          ))}
                          {/* Zahlungsplan is read-only in Controlling view */}
                        </div>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>

          </div>
        </div>

        {/* Legend - outside scroll container so it stays in place */}
        <div className="legend">
          <div className="legend-items">
            {projectPersons.map((personId) => (
              <div
                key={personId}
                className={`legend-item ${hoveredPerson === personId ? "highlighted" : ""}`}
                onMouseEnter={() => setHoveredPerson(personId)}
                onMouseLeave={() => setHoveredPerson(null)}
              >
                <span
                  className="legend-color"
                  style={{ backgroundColor: getPersonColor(personId) }}
                />
                <span
                  className="legend-name"
                  title={findTeamMember(teamMembersData, personId) ? undefined : `ID: ${personId}`}
                >
                  {getEmployeeName(personId)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Finanzen Section - Outside scroll container */}
        {phases.length > 0 && (
          <div className="finanzen-section">
            <h2 className="finanzen-title">Finanzen</h2>
            <div className="finanzen-table-wrapper">
              <table className="finanzen-table">
                <thead>
                  <tr>
                    <th className="phase-name-col">Phase</th>
                    {/* Ressourcen Section */}
                    <th
                      className="number-col budget-group-header clickable"
                      onClick={() => toggleSection('ressourcen')}
                      title="Planung: Verarbeitet = bereits geleistete Stunden. Geplant = im Planer eingetragene Stunden. Klicken zum Auf-/Zuklappen"
                    >
                      <div className="budget-header-group">
                        <span className="budget-group-title">Ressourcen</span>
                        <span className="toggle-indicator">{expandedSection === 'ressourcen' ? "▼" : "▶"}</span>
                      </div>
                      <div className="header-main">Planung</div>
                      <div className="header-subtitle">verarbeitet / geplant</div>
                    </th>
                    {expandedSection === 'ressourcen' && (
                      <>
                        <th className="number-col" title="Stundenbudget Offeriert: (Budget - Externe Kosten) / 145. Möglich: Ausblick / 135">
                          Stundenbudget
                          <div className="header-subtitle">Offeriert / möglich</div>
                        </th>
                        <th className="number-col" title="Differenz Planung: Geplante Stunden - Stundenbudget">
                          Differenz Planung
                          <div className="header-subtitle">Offeriert / möglich</div>
                        </th>
                        <th className="number-col" title={useExternalRate ? "Externe Kosten: Arbeitsstunden × Externer Stundensatz. Prognose: Geplante Stunden × Externer Stundensatz" : "Interne Kosten: Arbeitsstunden × Interner Stundensatz. Prognose: Geplante Stunden × Interner Stundensatz"}>
                          {useExternalRate ? "Externe Kosten" : "Interne Kosten"}
                          <div className="header-subtitle">Bisher / Prognose</div>
                        </th>
                      </>
                    )}
                    {/* Budget Section */}
                    <th
                      className="number-col budget-group-header clickable"
                      onClick={() => toggleSection('budget')}
                      title="Offeriert Intern: Budget - Externes Budget. Offeriert Extern: Externes Budget (Subplaner). Klicken zum Auf-/Zuklappen"
                    >
                      <div className="budget-header-group">
                        <span className="budget-group-title">Budget</span>
                        <span className="toggle-indicator">{expandedSection === 'budget' ? "▼" : "▶"}</span>
                      </div>
                      <div className="header-main">Offeriert</div>
                      <div className="header-subtitle">Eigenanteil / Dritte</div>
                    </th>
                    {expandedSection === 'budget' && (
                      <>
                        <th className="number-col" title="Bezahlt: Summe aller Einnahmen/Ausgaben mit Status 'paid'">
                          Bezahlt
                          <div className="header-subtitle">Einnahmen Total / Dritte</div>
                        </th>
                        <th className="number-col" title="In Rechnung: Einnahmen mit Status 'sent'/'overdue', Subplaner mit Status 'received'/'expected'">
                          In Rechnung
                          <div className="header-subtitle">Einnahmen Total / Dritte</div>
                        </th>
                        <th className="number-col" title="Budgetiert: Einnahmen mit Status 'expected', Subplaner: zukünftige Einträge ohne Status">
                          Budgetiert
                          <div className="header-subtitle">Einnahmen / Ausgaben</div>
                        </th>
                      </>
                    )}
                    {/* Ausblick - always visible */}
                    <th className="number-col" title={useExternalRate ? "Ausblick Extern: Alle Einnahmen - Alle Ausgaben - Externe Kosten Bisher - MAX(0, Externe Kosten Prognose)" : "Ausblick Intern: Alle Einnahmen - Alle Ausgaben - Interne Kosten Bisher - MAX(0, Interne Kosten Prognose)"}>
                      {useExternalRate ? "Ausblick Extern" : "Ausblick Intern"}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {phases.map((phase) => {
                    const financials = getPhaseFinancials(phase);
                    const isExpanded = expandedFinancePhases.includes(phase.id);
                    // Stunden-Budget = (total budget - external budget) / 145
                    const stundenBudget = (financials.budget - financials.externalBudget) / 145;
                    // Differenz Planung uses hoursAdjustedPlan (verarbeitet + zukünftig geplant)
                    const differenzPlanung = stundenBudget - financials.hoursAdjustedPlan;
                    // Offeriert: Intern = Budget - Externe Kosten, Extern = Externe Kosten
                    const offeriertIntern = financials.budget - financials.externalBudget;
                    const offeriertExtern = financials.externalBudget;

                    // M&F Ausblick = budget - (Planung verarbeitet * rate) - ((Planung geplant - Planung verarbeitet) * rate)
                    // = offeriertIntern - internalCost - internalCostPlannedFuture
                    const mfAusblickIntern = offeriertIntern - financials.internalCost - financials.internalCostPlannedFuture;
                    const mfAusblickExtern = offeriertIntern - financials.externalCost - financials.externalCostPlannedFuture;

                    // Partner Ausblick = offeriert - bezahlt - inRechnung - budgetiert (calculated per partner)
                    const sumPartnerAusblick = financials.partnerDetails.reduce((sum, partner) => {
                      const partnerOfferiert = getPartnerBudget(phase, partner.partnerId);
                      const partnerAusblick = partnerOfferiert - partner.totalPaid - partner.totalInvoiced - partner.totalBudgeted;
                      return sum + (partnerOfferiert > 0 ? partnerAusblick : 0);
                    }, 0);

                    // Phase total Ausblick = M&F Ausblick + sum of Partner Ausblicks
                    const phaseAusblickIntern = mfAusblickIntern + sumPartnerAusblick;
                    const phaseAusblickExtern = mfAusblickExtern + sumPartnerAusblick;

                    // Aktuell calculations based on phase Ausblick
                    const stundenBudgetAktuell = stundenBudget + phaseAusblickIntern / 135;
                    const differenzPlanungAktuell = stundenBudgetAktuell - financials.hoursAdjustedPlan;

                    return (
                      <React.Fragment key={phase.id}>
                        <tr
                          className={`phase-finance-row ${isExpanded ? "expanded" : ""}`}
                          onClick={() => toggleFinancePhase(phase.id)}
                        >
                          <td className="phase-name-col">
                            <span className="toggle-icon">{isExpanded ? "▼" : "▶"}</span>
                            <span className="phase-name">{phase.name || "Unbenannt"}</span>
                          </td>
                          {/* Ressourcen Section - Planung: verarbeitet / (verarbeitet + zukünftig geplant) */}
                          <td className="number-col">{Math.round(financials.hoursWorked)}h / {Math.round(financials.hoursAdjustedPlan)}h</td>
                          {expandedSection === 'ressourcen' && (
                            <>
                              <td className="number-col">
                                {Math.round(stundenBudget)}h / <span className={stundenBudgetAktuell < 0 ? "negative" : stundenBudgetAktuell > 0 ? "positive" : ""}>{Math.round(stundenBudgetAktuell)}h</span>
                              </td>
                              <td className="number-col">
                                <span className={differenzPlanung > 0 ? "positive" : differenzPlanung < 0 ? "negative" : ""}>{differenzPlanung > 0 ? "+" : ""}{Math.round(differenzPlanung)}h</span>
                                {" / "}
                                <span className={differenzPlanungAktuell < 0 ? "positive" : differenzPlanungAktuell > 0 ? "negative" : ""}>{differenzPlanungAktuell > 0 ? "+" : ""}{Math.round(differenzPlanungAktuell)}h</span>
                              </td>
                              <td className={`number-col ${(useExternalRate ? financials.externalCost : financials.internalCost) > financials.budget ? "negative" : ""}`}>
                                {formatCurrency(useExternalRate ? financials.externalCost : financials.internalCost)} / {formatCurrency(useExternalRate ? financials.externalCostPlannedFuture : financials.internalCostPlannedFuture)}
                              </td>
                            </>
                          )}
                          {/* Budget Section */}
                          <td className="number-col">{formatCurrency(offeriertIntern)} / {formatCurrency(offeriertExtern)}</td>
                          {expandedSection === 'budget' && (
                            <>
                              <td className="number-col">{formatCurrency(financials.incomePaid)} / {formatCurrency(financials.expensesPaid)}</td>
                              <td className="number-col">{formatCurrency(financials.incomeSent)} / {formatCurrency(financials.expensesReceived)}</td>
                              <td className="number-col">{formatCurrency(financials.incomeBudgeted)} / {formatCurrency(financials.expensesBudgeted)}</td>
                            </>
                          )}
                          {/* Ausblick - always visible (Phase total = M&F + Partners) */}
                          <td className={`number-col ${(useExternalRate ? phaseAusblickExtern : phaseAusblickIntern) < 0 ? "negative" : (useExternalRate ? phaseAusblickExtern : phaseAusblickIntern) > 0 ? "positive" : ""}`}>
                            {formatCurrency(useExternalRate ? phaseAusblickExtern : phaseAusblickIntern)}
                          </td>
                        </tr>

                        {/* Expanded details - show persons for Ressourcen, partners for Budget */}
                        {isExpanded && expandedSection !== 'budget' && financials.personDetails.map((person) => {
                          const personStundenBudget = ((financials.budget - financials.externalBudget) / 145) * (person.hoursBudgeted / (financials.hoursBudgeted || 1));
                          const personDifferenzPlanung = personStundenBudget - person.hoursBudgeted;
                          const phasePersonProductUuid = getPhasePersonProduct(phase, person.personId);
                          const phasePersonProduct = phasePersonProductUuid ? getProductInfo(phasePersonProductUuid) : null;
                          return (
                            <tr key={`${phase.id}-${person.personId}`} className="person-finance-row">
                              <td className="phase-name-col person-indent">
                                <span
                                  className="person-color-dot"
                                  style={{ backgroundColor: getPersonColor(person.personId) }}
                                />
                                <span
                                  className={`person-name ${!readOnly ? 'clickable' : ''}`}
                                  onClick={(e) => !readOnly && handlePhasePersonProductClick(e, phase, person.personId, person.name)}
                                  title={!readOnly ? "Klicken um Leistung für diese Phase zu bearbeiten" : undefined}
                                >
                                  {person.name}
                                </span>
                                {!readOnly && (person.internalRate > 0 || phasePersonProduct) && (
                                  <span className="rate-info">
                                    ({person.internalRate} / {phasePersonProduct?.cost || person.externalRate || "-"} CHF/h)
                                    {phasePersonProduct && <span className="phase-rate-indicator" title={`Phase: ${phasePersonProduct.productKey}`}>*</span>}
                                  </span>
                                )}
                              </td>
                              {/* Ressourcen Section */}
                              <td className="number-col">{Math.round(person.hoursWorked)}h / {Math.round(person.hoursWorked + person.hoursFutureBudgeted)}h</td>
                              {expandedSection === 'ressourcen' && (
                                <>
                                  <td className="number-col">-</td>
                                  <td className={`number-col ${person.hoursDiff < 0 ? "positive" : person.hoursDiff > 0 ? "negative" : ""}`}>
                                    {person.hoursDiff > 0 ? "+" : ""}{Math.round(person.hoursDiff)}h
                                  </td>
                                  <td className="number-col">
                                    {formatCurrency(useExternalRate ? person.externalCost : person.internalCost)} / {formatCurrency(useExternalRate ? (person.externalCostPlanned - person.externalCost) : (person.internalCostPlanned - person.internalCost))}
                                  </td>
                                </>
                              )}
                              {/* Budget Section - not shown for person rows when budget expanded */}
                              <td className="number-col">-</td>
                              {/* Ausblick - always visible */}
                              <td className="number-col">-</td>
                            </tr>
                          );
                        })}

                        {/* Malheur&Fortuna row - shown at top when Budget column is expanded */}
                        {isExpanded && expandedSection === 'budget' && (() => {
                          // M&F Offeriert = Gesamtbudget - Externes Budget
                          const mfOfferiert = offeriertIntern;
                          // M&F Bezahlt = Einnahmen (paid) - Ausgaben (paid)
                          const mfBezahlt = financials.incomePaid - financials.expensesPaid;
                          // M&F Budgetiert = future hours × rate (Planung geplant - Planung verarbeitet) * rate
                          const mfBudgetiertIntern = financials.internalCostPlannedFuture;
                          const mfBudgetiertExtern = financials.externalCostPlannedFuture;

                          return (
                            <tr key={`${phase.id}-mf`} className="person-finance-row mf-row">
                              <td className="phase-name-col person-indent">
                                <span className="mf-name">Malheur&Fortuna</span>
                              </td>
                              {/* Ressourcen Section - empty */}
                              <td className="number-col">-</td>
                              {/* Budget Section */}
                              <td className="number-col">{formatCurrency(mfOfferiert)}</td>
                              <td className="number-col" title="Bezahlt: Einnahmen (paid) - Ausgaben (paid)">
                                {formatCurrency(mfBezahlt)}
                              </td>
                              <td className="number-col">-</td>
                              <td className="number-col" title={useExternalRate ? "Budgetiert: zukünftige Stunden × externer Stundensatz" : "Budgetiert: zukünftige Stunden × interner Stundensatz"}>
                                {formatCurrency(useExternalRate ? mfBudgetiertExtern : mfBudgetiertIntern)}
                              </td>
                              {/* Ausblick: Offeriert - (hours worked * rate) - (future hours * rate) */}
                              <td className={`number-col ${(useExternalRate ? mfAusblickExtern : mfAusblickIntern) < 0 ? "negative" : (useExternalRate ? mfAusblickExtern : mfAusblickIntern) > 0 ? "positive" : ""}`}>
                                {formatCurrency(useExternalRate ? mfAusblickExtern : mfAusblickIntern)}
                              </td>
                            </tr>
                          );
                        })()}

                        {/* Expanded partner details - shown when Budget column is expanded */}
                        {isExpanded && expandedSection === 'budget' && financials.partnerDetails.map((partner) => {
                          // Get the partner's offeriert budget from phase (editable)
                          const partnerOfferiert = getPartnerBudget(phase, partner.partnerId);
                          // Ausblick Intern = Offeriert - Bezahlt - In Rechnung - Budgetiert
                          const partnerAusblickIntern = partnerOfferiert - partner.totalPaid - partner.totalInvoiced - partner.totalBudgeted;
                          const isEditing = editingPartnerBudget?.phaseId === phase.id && editingPartnerBudget?.partnerId === partner.partnerId;

                          return (
                            <tr key={`${phase.id}-partner-${partner.partnerId}`} className="person-finance-row partner-row">
                              <td className="phase-name-col person-indent">
                                <span className="partner-name">{partner.partnerName}</span>
                              </td>
                              {/* Ressourcen Section - empty for partners */}
                              <td className="number-col">-</td>
                              {/* Budget Section - Offeriert (editable) */}
                              <td
                                className="number-col editable-cell"
                                onClick={() => setEditingPartnerBudget({ phaseId: phase.id, partnerId: partner.partnerId })}
                                title="Klicken um Budget zu bearbeiten"
                              >
                                {isEditing ? (
                                  <input
                                    type="number"
                                    className="partner-budget-input"
                                    defaultValue={partnerOfferiert || ""}
                                    autoFocus
                                    onBlur={(e) => handlePartnerBudgetChange(phase.id, partner.partnerId, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handlePartnerBudgetChange(phase.id, partner.partnerId, e.target.value);
                                      } else if (e.key === 'Escape') {
                                        setEditingPartnerBudget(null);
                                      }
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                ) : (
                                  <span className="clickable-value">
                                    {partnerOfferiert > 0 ? formatCurrency(partnerOfferiert) : "-"}
                                  </span>
                                )}
                              </td>
                              <td className="number-col">{formatCurrency(partner.totalPaid)}</td>
                              <td className="number-col">{formatCurrency(partner.totalInvoiced)}</td>
                              <td className="number-col">{formatCurrency(partner.totalBudgeted)}</td>
                              {/* Ausblick - always visible (partners use same value for both intern/extern) */}
                              <td className={`number-col ${partnerAusblickIntern < 0 ? "negative" : partnerAusblickIntern > 0 ? "positive" : ""}`}>
                                {partnerOfferiert > 0 ? formatCurrency(partnerAusblickIntern) : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}

                  {/* Spacer row before Total */}
                  <tr className="spacer-row">
                    <td colSpan="100" style={{ height: '40px', background: 'transparent', border: 'none' }}></td>
                  </tr>

                  {/* Total row */}
                  {(() => {
                    const allFinancials = phases.map(p => getPhaseFinancials(p));
                    const totalHoursWorked = allFinancials.reduce((sum, f) => sum + f.hoursWorked, 0);
                    const totalHoursAdjustedPlan = allFinancials.reduce((sum, f) => sum + f.hoursAdjustedPlan, 0);
                    const totalBudget = allFinancials.reduce((sum, f) => sum + f.budget, 0);
                    const totalExternalBudget = allFinancials.reduce((sum, f) => sum + f.externalBudget, 0);
                    // Stunden-Budget = (total budget - external budget) / 145
                    const totalStundenBudget = (totalBudget - totalExternalBudget) / 145;
                    const totalDifferenzPlanung = totalStundenBudget - totalHoursAdjustedPlan;
                    const totalInternalCost = allFinancials.reduce((sum, f) => sum + f.internalCost, 0);
                    const totalExternalCost = allFinancials.reduce((sum, f) => sum + f.externalCost, 0);
                    const totalInternalCostPlanned = allFinancials.reduce((sum, f) => sum + f.internalCostPlanned, 0);
                    const totalExternalCostPlanned = allFinancials.reduce((sum, f) => sum + f.externalCostPlanned, 0);
                    const totalInternalCostPlannedFuture = allFinancials.reduce((sum, f) => sum + f.internalCostPlannedFuture, 0);
                    const totalExternalCostPlannedFuture = allFinancials.reduce((sum, f) => sum + f.externalCostPlannedFuture, 0);
                    const totalIncomeBudgeted = allFinancials.reduce((sum, f) => sum + f.incomeBudgeted, 0);
                    const phasedExpensesBudgeted = allFinancials.reduce((sum, f) => sum + f.expensesBudgeted, 0);
                    // Add automatic (phase-less) expenses to Budgetiert
                    const automaticExpensesBudgeted = allBudgetEntries
                      .filter(e => e.typ === "Ausgabe" && (!e.phase || e.phase === ""))
                      .filter(e => !e.status || e.status === "" || e.status === "expected" || e.status === "expecting")
                      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
                    const totalExpensesBudgeted = phasedExpensesBudgeted + automaticExpensesBudgeted;
                    const totalIncomePaid = allFinancials.reduce((sum, f) => sum + f.incomePaid, 0);
                    const totalExpensesPaid = allFinancials.reduce((sum, f) => sum + f.expensesPaid, 0);
                    const totalIncomeSent = allFinancials.reduce((sum, f) => sum + f.incomeSent, 0);
                    const totalExpensesReceived = allFinancials.reduce((sum, f) => sum + f.expensesReceived, 0);
                    const totalIncomeTotal = allFinancials.reduce((sum, f) => sum + f.incomeTotal, 0);
                    const phasedExpenseTotal = allFinancials.reduce((sum, f) => sum + f.expenseTotal, 0);
                    // Add automatic (phase-less) expenses to total
                    const automaticExpenseTotal = allBudgetEntries
                      .filter(e => e.typ === "Ausgabe" && (!e.phase || e.phase === ""))
                      .reduce((sum, e) => sum + (e.netto_betrag || e.betrag || 0), 0);
                    const totalExpenseTotal = phasedExpenseTotal + automaticExpenseTotal;
                    // Offeriert totals
                    const totalOfferiertIntern = totalBudget - totalExternalBudget;
                    const totalOfferiertExtern = totalExternalBudget;

                    // Sum phase Ausblicks (M&F + Partners for each phase)
                    const totalAusblickIntern = phases.reduce((sum, phase) => {
                      const f = getPhaseFinancials(phase);
                      const phaseOfferiertIntern = f.budget - f.externalBudget;
                      // M&F Ausblick
                      const mfAusblick = phaseOfferiertIntern - f.internalCost - f.internalCostPlannedFuture;
                      // Partner Ausblicks
                      const partnerAusblicks = f.partnerDetails.reduce((pSum, partner) => {
                        const partnerOfferiert = getPartnerBudget(phase, partner.partnerId);
                        const partnerAusblick = partnerOfferiert - partner.totalPaid - partner.totalInvoiced - partner.totalBudgeted;
                        return pSum + (partnerOfferiert > 0 ? partnerAusblick : 0);
                      }, 0);
                      return sum + mfAusblick + partnerAusblicks;
                    }, 0);

                    const totalAusblickExtern = phases.reduce((sum, phase) => {
                      const f = getPhaseFinancials(phase);
                      const phaseOfferiertIntern = f.budget - f.externalBudget;
                      // M&F Ausblick (uses internal budget but external costs)
                      const mfAusblick = phaseOfferiertIntern - f.externalCost - f.externalCostPlannedFuture;
                      // Partner Ausblicks (same for both views)
                      const partnerAusblicks = f.partnerDetails.reduce((pSum, partner) => {
                        const partnerOfferiert = getPartnerBudget(phase, partner.partnerId);
                        const partnerAusblick = partnerOfferiert - partner.totalPaid - partner.totalInvoiced - partner.totalBudgeted;
                        return pSum + (partnerOfferiert > 0 ? partnerAusblick : 0);
                      }, 0);
                      return sum + mfAusblick + partnerAusblicks;
                    }, 0);

                    // Aktuell calculations based on Ausblick
                    const totalStundenBudgetAktuell = totalAusblickIntern / 135;
                    const totalDifferenzPlanungAktuell = totalHoursAdjustedPlan - totalStundenBudgetAktuell;

                    return (
                      <tr className="total-finance-row">
                        <td className="phase-name-col">
                          <strong>Total</strong>
                        </td>
                        {/* Ressourcen Section - Planung: verarbeitet / (verarbeitet + zukünftig geplant) */}
                        <td className="number-col">
                          <strong>{Math.round(totalHoursWorked)}h / {Math.round(totalHoursAdjustedPlan)}h</strong>
                        </td>
                        {expandedSection === 'ressourcen' && (
                          <>
                            <td className="number-col">
                              <strong>
                                {Math.round(totalStundenBudget)}h / <span className={totalStundenBudgetAktuell < 0 ? "negative" : totalStundenBudgetAktuell > 0 ? "positive" : ""}>{Math.round(totalStundenBudgetAktuell)}h</span>
                              </strong>
                            </td>
                            <td className="number-col">
                              <strong>
                                <span className={totalDifferenzPlanung > 0 ? "positive" : totalDifferenzPlanung < 0 ? "negative" : ""}>{totalDifferenzPlanung > 0 ? "+" : ""}{Math.round(totalDifferenzPlanung)}h</span>
                                {" / "}
                                <span className={totalDifferenzPlanungAktuell < 0 ? "positive" : totalDifferenzPlanungAktuell > 0 ? "negative" : ""}>{totalDifferenzPlanungAktuell > 0 ? "+" : ""}{Math.round(totalDifferenzPlanungAktuell)}h</span>
                              </strong>
                            </td>
                            <td className={`number-col ${(useExternalRate ? totalExternalCost : totalInternalCost) > totalBudget ? "negative" : ""}`}>
                              <strong>{formatCurrency(useExternalRate ? totalExternalCost : totalInternalCost)} / {formatCurrency(useExternalRate ? totalExternalCostPlannedFuture : totalInternalCostPlannedFuture)}</strong>
                            </td>
                          </>
                        )}
                        {/* Budget Section */}
                        <td className="number-col">
                          <strong>{formatCurrency(totalOfferiertIntern)} / {formatCurrency(totalOfferiertExtern)}</strong>
                        </td>
                        {expandedSection === 'budget' && (
                          <>
                            <td className="number-col">
                              <strong>{formatCurrency(totalIncomePaid)} / {formatCurrency(totalExpensesPaid)}</strong>
                            </td>
                            <td className="number-col">
                              <strong>{formatCurrency(totalIncomeSent)} / {formatCurrency(totalExpensesReceived)}</strong>
                            </td>
                            <td className="number-col">
                              <strong>{formatCurrency(totalIncomeBudgeted)} / {formatCurrency(totalExpensesBudgeted)}</strong>
                            </td>
                          </>
                        )}
                        {/* Ausblick - always visible */}
                        <td className={`number-col ${(useExternalRate ? totalAusblickExtern : totalAusblickIntern) < 0 ? "negative" : (useExternalRate ? totalAusblickExtern : totalAusblickIntern) > 0 ? "positive" : ""}`}>
                          <strong>{formatCurrency(useExternalRate ? totalAusblickExtern : totalAusblickIntern)}</strong>
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
            {/* Toggle button for Internal/External rate view - hidden for read-only users */}
            {!readOnly && (
              <div className="rate-toggle-container">
                <button
                  className={`rate-toggle-button ${useExternalRate ? 'extern' : 'intern'}`}
                  onClick={() => setUseExternalRate(!useExternalRate)}
                  title={useExternalRate ? "Zur internen Ansicht wechseln" : "Zur externen Ansicht wechseln"}
                >
                  {useExternalRate ? "Externe Ansicht" : "Interne Ansicht"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Phase Modal */}
      {showPhaseModal && selectedPhase && (
        <div className="modal-overlay" onClick={() => setShowPhaseModal(false)}>
          <div className="phase-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedPhase.id ? "Phase bearbeiten" : "Neue Phase"}</h3>
              <button
                className="close-button"
                onClick={() => setShowPhaseModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label>Phasenname</label>
                <input
                  type="text"
                  value={selectedPhase.name || ""}
                  onChange={(e) =>
                    setSelectedPhase({ ...selectedPhase, name: e.target.value })
                  }
                  placeholder="z.B. Konzeptphase"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Startdatum</label>
                  <input
                    type="date"
                    value={selectedPhase.startDate || ""}
                    onChange={(e) =>
                      setSelectedPhase({ ...selectedPhase, startDate: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Enddatum</label>
                  <input
                    type="date"
                    value={selectedPhase.endDate || ""}
                    onChange={(e) =>
                      setSelectedPhase({ ...selectedPhase, endDate: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Gesamtbudget (CHF)</label>
                  <input
                    type="number"
                    value={selectedPhase.budget || ""}
                    onChange={(e) =>
                      setSelectedPhase({ ...selectedPhase, budget: e.target.value })
                    }
                    placeholder="0"
                  />
                </div>
                <div className="form-group">
                  <label>Externes Budget (CHF)</label>
                  <input
                    type="number"
                    value={selectedPhase.externalBudget || ""}
                    onChange={(e) =>
                      setSelectedPhase({ ...selectedPhase, externalBudget: e.target.value })
                    }
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {!readOnly && selectedPhase.id && (
                <button
                  className="btn btn-danger"
                  onClick={handleDeletePhase}
                >
                  Löschen
                </button>
              )}
              <div className="footer-right">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPhaseModal(false)}
                >
                  {readOnly ? 'Schliessen' : 'Abbrechen'}
                </button>
                {!readOnly && (
                  <button className="btn btn-primary" onClick={handleSavePhase}>
                    Speichern
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rate Modal */}
      {showRateModal && selectedPersonForRate && (
        <div className="modal-overlay" onClick={() => setShowRateModal(false)}>
          <div className="rate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Externer Stundensatz</h3>
              <button
                className="close-button"
                onClick={() => setShowRateModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="person-info">
                <span
                  className="color-dot large"
                  style={{ backgroundColor: getPersonColor(selectedPersonForRate) }}
                />
                <span className="person-name-large">{getEmployeeName(selectedPersonForRate)}</span>
              </div>
              <div className="form-group">
                <label>Stundensatz (CHF/h)</label>
                <input
                  type="number"
                  min="0"
                  step="5"
                  value={personRates[selectedPersonForRate] || ""}
                  onChange={(e) => {
                    const newRate = parseFloat(e.target.value) || 0;
                    setPersonRates((prev) => ({
                      ...prev,
                      [selectedPersonForRate]: newRate,
                    }));
                  }}
                  placeholder="z.B. 150"
                  autoFocus
                />
                <span className="form-hint">
                  Dieser Stundensatz wird automatisch für die Zeiterfassung verwendet.
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <div className="footer-right">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowRateModal(false)}
                >
                  {readOnly ? 'Schliessen' : 'Abbrechen'}
                </button>
                {!readOnly && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleSaveRate(selectedPersonForRate, personRates[selectedPersonForRate] || 0)}
                  >
                    Speichern
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* External Rate Modal (Finanzen) */}
      {showExternalRateModal && selectedFinancePerson && (
        <div className="modal-overlay" onClick={() => setShowExternalRateModal(false)}>
          <div className="rate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Externer Stundensatz</h3>
              <button
                className="close-button"
                onClick={() => setShowExternalRateModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="person-info">
                <span
                  className="color-dot large"
                  style={{ backgroundColor: getPersonColor(selectedFinancePerson.personId) }}
                />
                <span className="person-name-large">{selectedFinancePerson.name}</span>
              </div>
              <div className="form-group">
                <label>Leistung (Produkt)</label>
                <select
                  value={selectedFinancePerson.productUuid || ""}
                  onChange={(e) => {
                    setSelectedFinancePerson(prev => ({
                      ...prev,
                      productUuid: e.target.value || null,
                    }));
                  }}
                >
                  <option value="">-- Keine Leistung --</option>
                  {products.map(product => (
                    <option key={product.uuid} value={product.uuid}>
                      {product.fullDisplayName} ({product.cost} CHF/h)
                    </option>
                  ))}
                </select>
                <span className="form-hint">
                  Der Stundensatz wird aus der gewählten Leistung übernommen.
                </span>
              </div>
              {selectedFinancePerson.productUuid && (
                <div className="selected-rate-info">
                  <strong>Stundensatz:</strong>{" "}
                  {products.find(p => p.uuid === selectedFinancePerson.productUuid)?.cost || 0} CHF/h
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="footer-right">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowExternalRateModal(false)}
                >
                  {readOnly ? 'Schliessen' : 'Abbrechen'}
                </button>
                {!readOnly && (
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveExternalRate}
                  >
                    Speichern
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Budget Modal (Zahlungsplan) */}
      {showBudgetModal && (
        <div className="modal-overlay" onClick={handleBudgetModalClose}>
          <div className="budget-modal-container" onClick={(e) => e.stopPropagation()}>
            <BudgetVerwaltung
              showFormDirectly={true}
              presetData={budgetModalPreset}
              onClose={handleBudgetModalClose}
              readOnly={readOnly}
            />
          </div>
        </div>
      )}

      {/* Phase Person Product Modal */}
      {showPhaseRateModal && selectedPhaseForRate && (
        <div className="modal-overlay" onClick={() => setShowPhaseRateModal(false)}>
          <div className="rate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Leistung für Phase</h3>
              <button
                className="close-button"
                onClick={() => setShowPhaseRateModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="person-info">
                <span
                  className="color-dot large"
                  style={{ backgroundColor: getPersonColor(selectedPhaseForRate.personId) }}
                />
                <span className="person-name-large">{selectedPhaseForRate.personName}</span>
              </div>
              <div className="phase-info">
                <span className="phase-label">Phase:</span>
                <span className="phase-name-large">{selectedPhaseForRate.phase?.name}</span>
                <span className="phase-dates">
                  {selectedPhaseForRate.phase?.startDate} - {selectedPhaseForRate.phase?.endDate}
                </span>
              </div>
              <div className="form-group">
                <label>Leistung (Produkt)</label>
                <select
                  value={selectedPhaseForRate.productUuid || ""}
                  onChange={(e) => setSelectedPhaseForRate(prev => ({
                    ...prev,
                    productUuid: e.target.value || null
                  }))}
                >
                  <option value="">-- Keine Leistung --</option>
                  {products
                    .filter(p => p.isService && !p.archivedAt)
                    .map(product => (
                      <option key={product.uuid} value={product.uuid}>
                        {product.productKey} - {product.translations?.de?.productName || product.productDescription} ({product.cost} CHF/h)
                      </option>
                    ))}
                </select>
                <span className="form-hint">
                  Diese Leistung wird bei der Zeiterfassung automatisch ausgewählt, wenn diese Phase aktiv ist.
                </span>
              </div>
              {selectedPhaseForRate.productUuid && (
                <div className="selected-rate-info">
                  <strong>Stundensatz:</strong>{" "}
                  {products.find(p => p.uuid === selectedPhaseForRate.productUuid)?.cost || 0} CHF/h
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="footer-right">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPhaseRateModal(false)}
                >
                  {readOnly ? 'Schliessen' : 'Abbrechen'}
                </button>
                {!readOnly && (
                  <button
                    className="btn btn-primary"
                    onClick={handleSavePhaseProduct}
                  >
                    Speichern
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zahlungsplan Modal */}
      {showZahlungsplanModal && (
        <ZahlungsplanModal
          project={project}
          projectCode={projectCode}
          phases={phases}
          allBudgetEntries={allBudgetEntries}
          onClose={() => setShowZahlungsplanModal(false)}
          onBudgetChange={handleBudgetModalClose}
          readOnly={readOnly}
        />
      )}

      {/* Ansätze Modal */}
      {showAnsaetzeModal && (
        <div className="modal-overlay" onClick={() => setShowAnsaetzeModal(false)}>
          <div className="ansaetze-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ansätze — {project?.title || projectCode}</h3>
              <button
                className="close-button"
                onClick={() => setShowAnsaetzeModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <ProjectRates
                project={project}
                projectCode={projectCode}
                readOnly={readOnly}
              />
            </div>
          </div>
        </div>
      )}

      {/* Firmen Modal — Auftraggeberin/Partner/Consultant/Contractor als echte
          CRM-Zuordnung (projectRelations, targetType "partner"), inkl. optionaler
          Phasen-Einschränkung. Ohne Phasen-Auswahl gilt eine Firma für alle Phasen. */}
      {showFirmenModal && (
        <div className="modal-overlay" onClick={() => setShowFirmenModal(false)}>
          <div className="ansaetze-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Firmen — {project?.title || projectCode}</h3>
              <button
                className="close-button"
                onClick={() => setShowFirmenModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p className="field-hint" style={{ marginBottom: "12px" }}>
                Firmen ohne Phasen-Auswahl gelten für alle Phasen dieses Projekts.
              </p>
              <PartnerAssignField
                projectCode={projectCode}
                roleOptions={PARTNER_ROLE_OPTIONS}
                phases={phases}
                emptyHint="Noch keine Firma zugeordnet."
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
      )}

      {/* Zuordnung Modal — gemeinsame Zuordnung (projectEmployeeAssignments) wie
          Pendenzen/Termine/Kostenschätzung (ADR 0020). Zeigt nur aktuell verfügbare
          Team-Mitglieder, keine Ansätze/Standard-Service mehr — die werden über den
          separaten "Ansätze"-Button gepflegt. */}
      {showAssignmentModal && (
        <PendenzMembersModal
          projectCode={projectCode}
          projectTitle={project?.title || projectCode}
          onClose={() => setShowAssignmentModal(false)}
          onChanged={async () => {
            try {
              const assignments = await getProjectAssignments(projectCode);
              setProjectAssignments(assignments);
            } catch (err) {
              console.error("Error reloading project assignments:", err);
            }
            setShowAssignmentModal(false);
          }}
        />
      )}
    </div>
  );
};

export default Controlling;
